import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from '../contexts/AuthProvider';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import ReportIssueModal from '../components/ReportIssueModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/data-table';
import { ColumnHeader } from '@/components/ui/column-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axiosInstance from '../utils/axiosInstance';
import { Search, Info, Flag, Plus, ChevronDown, ChevronRight, ExternalLink, Package, Link as LinkIcon, Link2, Store, TrendingDown, TrendingUp, Tag, Globe, Trash2, TicketPercent, CirclePlus, X, Loader2, Star, Check } from 'lucide-react';
import { useResellerImages } from '@/hooks/useResellerImages';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ReactCountryFlag from 'react-country-flag';
import { getCountryFlagCode } from '../utils/countryFlags';

// Custom Products Table Component with expandable rows
// Memoized to prevent unnecessary re-renders when parent state changes
const ProductsTable = memo(({ products, columns, expandedRows, productUrlsMap, loadingUrls, onToggleRow, onRowClick, getResellerFavicon, t, activeTab, onDeleteUrl, compact = false, sorting = { column: null, direction: null }, onSortChange }) => {
  return (
    <div className="rounded-md border">
      <table className="w-full">
        <thead>
          <tr className="border-b">
            {columns.map((column) => {
              const isNumeric = column.id === 'v_compareAtPrice';
              const isActions = column.id === 'actions';
              const isProduct = column.id === 'product';
              const columnId = column.id || column.accessorKey;
              const isSorted = sorting.column === columnId;
              const sortDirection = isSorted ? sorting.direction : false;
              const metaClassName = column.meta?.className || 'text-left';

              return (
                <th
                  key={columnId}
                  className={`${compact ? 'h-9 px-2' : 'h-12 px-4'} align-middle font-medium text-muted-foreground text-xs ${metaClassName}`}
                  style={{
                    minWidth: column.minSize ? `${column.minSize}px` : undefined,
                    width: isProduct ? undefined : (column.size ? `${column.size}px` : undefined),
                  }}
                >
                  {typeof column.header === 'function'
                    ? column.header({
                        column: {
                          columnDef: column,
                          getCanSort: () =>
                            column.enableSorting === true && typeof onSortChange === 'function',
                          getIsSorted: () =>
                            sorting.column === columnId ? sorting.direction : false,
                          toggleSorting: () => {
                            if (onSortChange && column.enableSorting === true) {
                              onSortChange(columnId);
                            }
                          },
                        },
                      })
                    : column.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const rowId = product.is_custom ? `custom_${product.custom_product_id}` : product.shopify_id;
            const isExpanded = expandedRows.has(rowId);
            const urls = productUrlsMap[rowId] || { twistscrape: [], brand_custom: [] };
            const isLoadingUrls = loadingUrls[rowId];
            const hasUrlsLoaded = productUrlsMap[rowId] !== undefined;
            
            return (
              <React.Fragment key={rowId}>
                <tr
                  className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                  onClick={() => onRowClick && onRowClick(product)}
                >
                  {columns.map((column) => {
                    const cellValue = column.accessorFn
                      ? column.accessorFn(product, 0)
                      : column.accessorKey
                      ? product[column.accessorKey]
                      : null;
                    
                    const isNumeric = column.id === 'v_compareAtPrice';
                    const isActions = column.id === 'actions';
                    const isProduct = column.id === 'product';
                    const metaClassName = column.meta?.className || 'text-left';

                    return (
                      <td
                        key={column.id || column.accessorKey}
                        className={`${compact ? 'py-1.5 px-2' : 'py-3 px-4'} align-middle ${metaClassName}`}
                        style={{
                          minWidth: column.minSize ? `${column.minSize}px` : undefined,
                          width: isProduct ? undefined : (column.size ? `${column.size}px` : undefined),
                        }}
                      >
                        {column.cell
                          ? column.cell({
                              getValue: () => cellValue,
                              row: { original: product },
                            })
                          : cellValue}
                      </td>
                    );
                  })}
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={columns.length} className="p-3 bg-muted/30">
                      <div className="space-y-3">
                        {isLoadingUrls ? (
                          <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                          </div>
                        ) : (
                          <>
                            {/* Twistscrape URLs */}
                            {urls.twistscrape && urls.twistscrape.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                                  {t('urls.urls')}
                                </h4>
                                <div className="space-y-2">
                                  {urls.twistscrape
                                    .sort((a, b) => (a.reseller_domain || '').localeCompare(b.reseller_domain || ''))
                                    .map((urlItem, idx) => (
                                    <div key={idx} className="flex items-center gap-3 p-2 border rounded text-sm">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        {urlItem.reseller_domain && getResellerFavicon(urlItem.reseller_domain) ? (
                                          <img
                                            src={getResellerFavicon(urlItem.reseller_domain)}
                                            alt={t('resellerFavicon')}
                                            className="w-4 h-4 rounded flex-shrink-0"
                                            onError={(e) => { 
                                              e.target.style.display = 'none';
                                              const icon = document.createElement('div');
                                              icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-link"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
                                              e.target.parentNode.insertBefore(icon.firstChild, e.target);
                                            }}
                                          />
                                        ) : (
                                          <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        )}
                                        <span className="font-medium truncate">{urlItem.reseller_domain || t('unknownReseller')}</span>
                                      </div>
                                      <a
                                        href={urlItem.original_url || urlItem.l_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline truncate max-w-md"
                                        onClick={(e) => e.stopPropagation()}
                                        title={urlItem.original_url || urlItem.l_url}
                                      >
                                        {urlItem.original_url || urlItem.l_url}
                                      </a>
                                      <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Custom Brand URLs */}
                            {urls.brand_custom && urls.brand_custom.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                                  {t('urls.sourceCustom')}
                                </h4>
                                <div className="space-y-2">
                                  {urls.brand_custom
                                    .sort((a, b) => (a.reseller_domain || '').localeCompare(b.reseller_domain || ''))
                                    .map((urlItem) => (
                                    <div key={urlItem.id} className="flex items-center justify-between p-2 border rounded text-sm">
                                      <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                          {urlItem.reseller_domain && getResellerFavicon(urlItem.reseller_domain) ? (
                                            <img
                                              src={getResellerFavicon(urlItem.reseller_domain)}
                                              alt={t('resellerFavicon')}
                                              className="w-4 h-4 rounded flex-shrink-0"
                                              onError={(e) => { 
                                                e.target.style.display = 'none';
                                                const icon = document.createElement('div');
                                                icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-link"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
                                                e.target.parentNode.insertBefore(icon.firstChild, e.target);
                                              }}
                                            />
                                          ) : (
                                            <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                          )}
                                          <span className="font-medium truncate">{urlItem.reseller_domain || t('unknownReseller')}</span>
                                        </div>
                                        <a
                                          href={urlItem.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary hover:underline truncate max-w-md"
                                          onClick={(e) => e.stopPropagation()}
                                          title={urlItem.url}
                                        >
                                          {urlItem.url}
                                        </a>
                                        <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onDeleteUrl(urlItem.id, rowId, product, urlItem);
                                        }}
                                        className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-red-600 transition-colors ml-2"
                                        title={t('urls.delete')}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {hasUrlsLoaded && 
                             (!urls.twistscrape || urls.twistscrape.length === 0) && 
                             (!urls.brand_custom || urls.brand_custom.length === 0) && (
                              <div className="text-center py-4 text-muted-foreground text-sm">
                                {t('urls.noUrls')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

const Products = () => {
  const { t } = useTranslation('products');
  const { t: tDashboard } = useTranslation('dashboard');
  const { toast } = useToast();
  const { user } = useAuth();
  const { getResellerImage } = useResellerImages();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'priceMonitoring';
  const initialResellerFilter = searchParams.get('reseller') || 'all';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false); // Loading state for filter changes
  const [error, setError] = useState(null);

  // Separate product lists for each tab
  const [customProducts, setCustomProducts] = useState([]);
  const [customProductsPagination, setCustomProductsPagination] = useState({ count: 0, page: 1, page_size: 20, total_pages: 1 });
  const [searchTerm, setSearchTerm] = useState('');
  // Debounce search term to avoid filtering on every keystroke (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [urlFilter, setUrlFilter] = useState('all'); // 'all', 'with_urls', 'without_urls' - for lamptwistProducts tab
  const [sorting, setSorting] = useState({ column: 'discount', direction: 'desc' }); // Sorting state; default shows biggest violations first on the priceMonitoring tab
  const [activeReports, setActiveReports] = useState({}); // { "shopify_123": 2, "ean_ABC": 1, ... }
  const [reportedValuesMap, setReportedValuesMap] = useState({}); // { "shopify_123": { rrp: 99.99, ean: "123...", vendor_code: "ABC" } }
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null); // { id, tab, row }
  const [reportIssues, setReportIssues] = useState({
    bad_url: false,
    wrong_rrp: false,
    wrong_ean: false,
    wrong_vendor_code: false,
  });
  const [reportRrp, setReportRrp] = useState('');
  const [reportEan, setReportEan] = useState('');
  const [reportVendorCode, setReportVendorCode] = useState('');
  const [hasExistingReports, setHasExistingReports] = useState(false);
  const [existingReportTypes, setExistingReportTypes] = useState({ bad_url: false, wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 20,
    total_pages: 1
  });
  
  // Frontend pagination for filtered results
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  // Pagination for Price Monitoring tab
  const [priceMonitoringPage, setPriceMonitoringPage] = useState(1);
  const [priceMonitoringItemsPerPage, setPriceMonitoringItemsPerPage] = useState(50);
  const [expandedRows, setExpandedRows] = useState(new Set()); // Set of shopify_ids
  const [productUrlsMap, setProductUrlsMap] = useState({}); // { shopify_id: { twistscrape: [], brand_custom: [] } }
  const [loadingUrls, setLoadingUrls] = useState({}); // { shopify_id: true/false }
  const [showAddUrlModal, setShowAddUrlModal] = useState(false);
  const [selectedProductForUrl, setSelectedProductForUrl] = useState(null);
  const [availableResellers, setAvailableResellers] = useState([]);
  const [uniqueResellersFromApi, setUniqueResellersFromApi] = useState([]);
  const [newUrl, setNewUrl] = useState({ url: '', ean: '' });
  const [urlInputs, setUrlInputs] = useState([{ id: 1, url: '', status: null }]); // Multi-URL support
  const [addingUrl, setAddingUrl] = useState(false);
  const [urlError, setUrlError] = useState('');
  
  // Custom Product Modal States
  const [showAddCustomProductModal, setShowAddCustomProductModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [newCustomProduct, setNewCustomProduct] = useState({
    name: '',
    variant_name: '',
    ean: '',
    rrp_incl_vat: '',
    vendor_code: '',
    urls: []
  });
  const [addingCustomProduct, setAddingCustomProduct] = useState(false);
  const [customProductError, setCustomProductError] = useState('');

  // Price Monitoring Tab States (from Dashboard)
  const [competitorData, setCompetitorData] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [selectedReseller, setSelectedReseller] = useState(initialResellerFilter);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [discountMin, setDiscountMin] = useState('');
  const [discountMax, setDiscountMax] = useState('');
  const [onlyChangeVSYesterday, setOnlyChangeVSYesterday] = useState(false);
  const [favoritesOnlyFilter, setFavoritesOnlyFilter] = useState(true);
  const [priceMonitoringLoading, setPriceMonitoringLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    total_products: 0,
    total_resellers: 0,
    avg_discount: 0,
    total_products_all: 0,
    price_changes_vs_yesterday: 0
  });
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);

  // Favorites state
  const [favoriteIds, setFavoriteIds] = useState({ shopify_ids: [], eans: [] });
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  // Pagination for Favorites tab (server-side)
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [favoritesItemsPerPage, setFavoritesItemsPerPage] = useState(50);

  // Favorites product list (simplified - products only, no competitor prices)
  const [favoriteProductsList, setFavoriteProductsList] = useState([]);
  const [favoritesPagination, setFavoritesPagination] = useState({
    count: 0,
    page: 1,
    page_size: 50,
    total_pages: 1
  });
  const [favoritesSearch, setFavoritesSearch] = useState('');
  const debouncedFavoritesSearch = useDebounce(favoritesSearch, 300);

  // Track which tabs have been loaded (to avoid showing loader on tab switch)
  const [tabsLoaded, setTabsLoaded] = useState({
    favorites: false,
    lamptwistProducts: false,
    custom: false,
    priceMonitoring: false
  });

  // Store total counts for each tab (since products state is shared and gets overwritten)
  const [tabCounts, setTabCounts] = useState({
    favorites: 0,
    lamptwistProducts: 0,
    tracked: 0, // Keep for filter badges
    missingUrls: 0, // Keep for filter badges
    custom: 0
  });

  // Memoized callback for product click to prevent re-renders
  const handleProductClick = useCallback((row) => {
    // Use custom_product_id for custom products, shopify_id for regular products
    // Open in new tab
    if (row.is_custom) {
      window.open(`/product/custom/${row.custom_product_id}`, '_blank');
    } else {
      const shopifyId = row.shopify_id || row.p_handle;
      window.open(`/product/${shopifyId}`, '_blank');
    }
  }, []);

  // Handler for sorting
  const handleSortChange = useCallback((columnId) => {
    setSorting(prev => {
      // If clicking on the same column, toggle direction
      if (prev.column === columnId) {
        if (prev.direction === 'asc') {
          return { column: columnId, direction: 'desc' };
        } else if (prev.direction === 'desc') {
          return { column: null, direction: null }; // Remove sorting
        }
      }
      // New column, start with ascending
      return { column: columnId, direction: 'asc' };
    });
    // Reset to first page for both the lamptwistProducts pager and the priceMonitoring pager
    setCurrentPage(1);
    setPriceMonitoringPage(1);
  }, []);

  const getRowId = (row) => {
    if (row.is_custom) return `custom_${row.custom_product_id}`;
    if (row.shopify_id !== undefined && row.shopify_id !== null) return String(row.shopify_id);
    return row.id ? String(row.id) : undefined;
  };

  const openReportModal = async (row, tabKey) => {
    const id = getRowId(row);
    if (!id) return;
    setReportTarget({ id, tab: tabKey, row });

    // Initialize default values
    const currentRrp = row?.v_compareAtPrice ?? row?.compare_at_price ?? '';
    const defaultRrp = currentRrp !== null && currentRrp !== undefined ? String(currentRrp) : '';
    const defaultEan = row?.v_ean || '';
    const defaultVendorCode = row?.v_vendor_code || '';

    // Initialize with default values (will be overridden if existing reports found)
    let issues = {
      bad_url: false,
      wrong_rrp: false,
      wrong_ean: false,
      wrong_vendor_code: false,
    };
    let existingTypes = {
      bad_url: false,
      wrong_rrp: false,
      wrong_ean: false,
      wrong_vendor_code: false,
    };
    let proposedRrp = defaultRrp;
    let proposedEan = defaultEan;
    let proposedVendorCode = defaultVendorCode;
    let hasExisting = false;

    // Load existing reports for this product BEFORE opening modal
    try {
      const shopify_id = row?.shopify_id;
      if (shopify_id !== undefined && shopify_id !== null) {
        const response = await axiosInstance.get(`/api/product-reports/list/?shopify_id=${shopify_id}`);
        const existingReports = response.data;

        // Check if there are existing reports
        hasExisting = existingReports.length > 0;

        // Pre-fill the form based on existing reports
        if (hasExisting) {
          existingReports.forEach(report => {
            if (report.issue_type === 'bad_url') {
              issues.bad_url = true;
              existingTypes.bad_url = true;
            } else if (report.issue_type === 'wrong_rrp') {
              issues.wrong_rrp = true;
              existingTypes.wrong_rrp = true;
              if (report.proposed_rrp) {
                proposedRrp = String(report.proposed_rrp);
              }
            } else if (report.issue_type === 'wrong_ean') {
              issues.wrong_ean = true;
              existingTypes.wrong_ean = true;
              if (report.proposed_ean) {
                proposedEan = report.proposed_ean;
              }
            } else if (report.issue_type === 'wrong_vendor_code') {
              issues.wrong_vendor_code = true;
              existingTypes.wrong_vendor_code = true;
              if (report.proposed_vendor_code) {
                proposedVendorCode = report.proposed_vendor_code;
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading existing reports:', error);
      // Silently fail - user can still create new reports
    }

    // Set all state values at once BEFORE opening modal
    setHasExistingReports(hasExisting);
    setExistingReportTypes(existingTypes);
    setReportIssues(issues);
    setReportRrp(proposedRrp);
    setReportEan(proposedEan);
    setReportVendorCode(proposedVendorCode);

    // Now open modal with all data already loaded
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportModalOpen(false);
    setReportTarget(null);
    setReportIssues({ bad_url: false, wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
    setReportRrp('');
    setReportEan('');
    setReportVendorCode('');
  };

  const deleteReport = async (issueType) => {
    if (!reportTarget) return;
    const { row } = reportTarget;

    try {
      const shopify_id = row?.shopify_id;
      if (shopify_id !== undefined && shopify_id !== null) {
        await axiosInstance.delete(`/api/product-reports/delete/`, {
          params: {
            shopify_id,
            issue_type: issueType
          }
        });

        toast({
          description: 'Report cancelled successfully',
        });

        // Update local state to uncheck the box and mark as not existing
        setReportIssues(prev => ({ ...prev, [issueType]: false }));
        setExistingReportTypes(prev => ({ ...prev, [issueType]: false }));

        // Check if there are any remaining reports
        const remainingReports = Object.entries(existingReportTypes).some(
          ([key, value]) => key !== issueType && value
        );
        setHasExistingReports(remainingReports);

        // Refresh data in parallel for better performance
        if (activeTab === 'priceMonitoring') {
          // For price monitoring tab, only refresh price monitoring data
          await Promise.all([
            fetchCompetitorPrices(),
            fetchDashboardStats(),
            fetchActiveReports(), // Still needed for report indicators
          ]);
        } else {
          // For other tabs, only refresh product lists
          await Promise.all([
            fetchProducts(currentPage, { tab: activeTab, search: debouncedSearchTerm }),
            fetchActiveReports(),
          ]);
        }
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: 'Failed to cancel report',
      });
    }
  };

  const applyReport = async () => {
    if (!reportTarget) return;
    const { row, tab } = reportTarget;

    if (!reportIssues.bad_url && !reportIssues.wrong_rrp && !reportIssues.wrong_ean && !reportIssues.wrong_vendor_code) return;

    try {
      // Prepare reports to send (can be multiple for bad_url, single for rrp/ean)
      const reportsToSend = [];

      // Reports are only for Lamptwist products which always have shopify_id
      // shopify_id can be 0, so we check for null/undefined only
      if (row?.shopify_id === undefined || row?.shopify_id === null) {
        console.error('❌ ERROR: No shopify_id available for report!');
        toast({
          variant: 'destructive',
          title: t('error'),
          description: 'Unable to create report. Product ID missing.',
        });
        return;
      }

      const shopify_id = row.shopify_id;

      if (reportIssues.bad_url && tab === 'priceMonitoring') {
        // For bad_url, we need the URL from the row
        const reportedUrl = (row?.url || row?.twistscrape_url || row?.product_url || '').trim();
        if (!reportedUrl) {
          toast({
            variant: 'destructive',
            title: t('error'),
            description: t('report.missingUrl'),
          });
          return;
        }

        const reportData = {
          issue_type: 'bad_url',
          reported_url: reportedUrl,
          shopify_id: shopify_id,
        };

        // Optional fields
        if (row?.v_compareAtPrice || row?.compare_at_price) {
          reportData.current_rrp = row?.v_compareAtPrice || row?.compare_at_price;
        }
        if (row?.v_ean) reportData.current_ean = row.v_ean;

        reportsToSend.push(reportData);
      }

      if (reportIssues.wrong_rrp) {
        const reportData = {
          issue_type: 'wrong_rrp',
          proposed_rrp: parseFloat(reportRrp),
          shopify_id: shopify_id,
          reported_url: null,  // Explicitly set to null for wrong_rrp reports
        };

        // Optional fields
        if (row?.v_compareAtPrice || row?.compare_at_price) {
          reportData.current_rrp = row?.v_compareAtPrice || row?.compare_at_price;
        }

        reportsToSend.push(reportData);
      }

      if (reportIssues.wrong_ean) {
        const reportData = {
          issue_type: 'wrong_ean',
          proposed_ean: reportEan.trim(),
          shopify_id: shopify_id,
          reported_url: null,  // Explicitly set to null for wrong_ean reports
        }

        // Optional fields
        if (row?.v_ean) reportData.current_ean = row.v_ean;
        if (row?.v_compareAtPrice || row?.compare_at_price) {
          reportData.current_rrp = row?.v_compareAtPrice || row?.compare_at_price;
        }

        reportsToSend.push(reportData);
      }

      if (reportIssues.wrong_vendor_code) {
        const reportData = {
          issue_type: 'wrong_vendor_code',
          proposed_vendor_code: reportVendorCode.trim(),
          shopify_id: shopify_id,
          reported_url: null,  // Explicitly set to null for wrong_vendor_code reports
        }

        // Optional fields
        if (row?.v_vendor_code) reportData.current_vendor_code = row.v_vendor_code;

        reportsToSend.push(reportData);
      }

      // Set loading state
      setSubmittingReport(true);

      // Send all reports to backend
      console.log('📤 FRONTEND: Sending reports to backend. Count:', reportsToSend.length);
      for (const reportData of reportsToSend) {
        console.log('📤 FRONTEND: Sending report data:', JSON.stringify(reportData, null, 2));
        await axiosInstance.post('/api/product-reports/', reportData);
      }

      // Update local state optimistically for immediate UI feedback
      const rowId = getRowId(row);
      if (reportIssues.wrong_rrp || reportIssues.wrong_ean || reportIssues.wrong_vendor_code) {
        // Update reportedValuesMap
        setReportedValuesMap(prev => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            ...(reportIssues.wrong_rrp && { rrp: parseFloat(reportRrp) }),
            ...(reportIssues.wrong_ean && { ean: reportEan.trim() }),
            ...(reportIssues.wrong_vendor_code && { vendor_code: reportVendorCode.trim() }),
          }
        }));

        // Update competitorData for Price Monitoring tab with recalculated discount
        if (activeTab === 'priceMonitoring' && reportIssues.wrong_rrp) {
          const newRrp = parseFloat(reportRrp);
          setCompetitorData(prev => prev.map(item => {
            if (String(item.shopify_id) === String(row.shopify_id)) {
              const priceReseller = item.price_reseller || item.price;
              const newDiscount = priceReseller && newRrp > 0
                ? ((newRrp - priceReseller) * 100 / newRrp).toFixed(2)
                : item.discount;
              return {
                ...item,
                compare_at_price: newRrp,
                discount: parseFloat(newDiscount),
              };
            }
            return item;
          }));
        }
      }

      // Close modal after updates
      closeReportModal();

      // Show success toast
      toast({
        description: t('report.successMessage'),
      });

      // Only refresh active reports count (lightweight)
      await fetchActiveReports();
    } catch (error) {
      console.error('Error submitting report:', error);

      // Extract error message from response
      let errorMessage = t('report.submitError');
      if (error.response?.data) {
        const data = error.response.data;
        // Handle validation errors
        if (data.non_field_errors) {
          // Handle duplicate report error
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        } else if (data.proposed_rrp) {
          errorMessage = Array.isArray(data.proposed_rrp) ? data.proposed_rrp[0] : data.proposed_rrp;
        } else if (data.proposed_ean) {
          errorMessage = Array.isArray(data.proposed_ean) ? data.proposed_ean[0] : data.proposed_ean;
        } else if (data.reported_url) {
          errorMessage = Array.isArray(data.reported_url) ? data.reported_url[0] : data.reported_url;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      }

      toast({
        variant: 'destructive',
        title: t('error'),
        description: errorMessage,
      });
    } finally {
      setSubmittingReport(false);
    }
  };

  // Track if initial load is done to prevent duplicate fetches
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Guards the filter-change refetch effect so it doesn't fire on its first
  // automatic trigger (e.g. when initial load sets selectedDate). Hoisted here
  // so the brand-change reset below can flip it back to false on a brand
  // switch, matching first-mount semantics for the new brand.
  const competitorFiltersInitialized = React.useRef(false);

  // Snapshot of the filter values used for the initial competitor-prices
  // fetch. Used by the filter-change effect to detect when the user toggled
  // a filter *during* the initial load — that toggle gets caught by the
  // not-yet-loaded early-return and would otherwise be silently dropped.
  const initialCompetitorFiltersSnapshot = React.useRef(null);

  // Tracks the brand we last fetched for. Used to distinguish "first mount"
  // from "brand changed in navbar" so we can reset filters + cached data
  // before refetching, instead of bleeding stale state across brands.
  const prevBrandRef = React.useRef(null);

  const favoritesOnlyStorageKey = (brand) => `priceMonitoring.favoritesOnly.${brand}`;

  const readFavoritesOnlyPref = (brand) => {
    if (!brand) return null;
    try {
      const v = localStorage.getItem(favoritesOnlyStorageKey(brand));
      if (v === 'true') return true;
      if (v === 'false') return false;
      return null;
    } catch {
      return null;
    }
  };

  const writeFavoritesOnlyPref = (brand, value) => {
    if (!brand) return;
    try {
      localStorage.setItem(favoritesOnlyStorageKey(brand), String(value));
    } catch {
      // localStorage may be disabled (private mode, quota)
    }
  };

  useEffect(() => {
    // Don't fetch until user/brand is available
    if (!user?.brand) {
      console.log('⏱️ [Products] Skipping fetch - no user/brand yet');
      return;
    }

    const brandChanged = prevBrandRef.current !== null && prevBrandRef.current !== user.brand;
    const previousBrand = prevBrandRef.current;
    prevBrandRef.current = user.brand;

    if (brandChanged) {
      console.log(`⏱️ [Products] Brand changed: ${previousBrand} -> ${user.brand}, resetting filters/data`);
      // Reset filters (favoritesOnly is decided after fetching favorites below)
      setProductFilter('');
      setSelectedReseller('all');
      setSelectedCountry('all');
      setDiscountMin('');
      setDiscountMax('');
      setOnlyChangeVSYesterday(false);
      setSelectedDate('');
      // Reset cached data (otherwise the previous brand's rows / resellers
      // briefly bleed into the new brand's tables and dropdowns)
      setCompetitorData([]);
      setUniqueResellersFromApi([]);
      setAvailableDates([]);
      setProducts([]);
      setFavoriteProductsList([]);
      // Reset pagination
      setPriceMonitoringPage(1);
      setFavoritesPage(1);
      setCurrentPage(1);
      // Force every tab to re-fetch on next visit
      setTabsLoaded({ favorites: false, lamptwistProducts: false, custom: false, priceMonitoring: false });
      // Reset guard so the refetch effect skips its first auto-trigger on the
      // new brand (matches initial-mount semantics)
      competitorFiltersInitialized.current = false;
      initialCompetitorFiltersSnapshot.current = null;
      setInitialLoadDone(false);
    }

    // Create abort controller to cancel requests if effect re-runs
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Load data on mount and when brand changes
    const start = performance.now();
    console.log('⏱️ [Products] === PAGE LOAD START ===');

    (async () => {
      // Decide the default "favorites only" filter:
      // - if the user has stored a preference for this brand, use it
      // - otherwise fall back to true when they already have favorites
      //   (existing users keep their current behavior), false otherwise.
      const favs = await fetchFavorites(signal);
      if (signal.aborted) return;
      const stored = readFavoritesOnlyPref(user.brand);
      const hasFavorites = ((favs?.shopify_ids?.length) || 0) + ((favs?.eans?.length) || 0) > 0;
      const favoritesOnlyForFetch = stored !== null ? stored : hasFavorites;
      setFavoritesOnlyFilter(favoritesOnlyForFetch);

      // Essential data that's always needed
      const essentialPromises = [
        fetchTabCounts(signal),
        fetchActiveReports(signal),
        fetchAvailableResellers(signal),
      ];

      // Load data for the initial active tab only
      if (activeTab === 'favorites') {
        essentialPromises.push(fetchFavoriteProductsList(1, 50, '', signal));
      } else if (activeTab === 'priceMonitoring') {
        // Snapshot the filter values the initial fetch is going out with, so
        // the filter-change effect can detect filter toggles made by the user
        // before initial load completed and refetch on their behalf.
        initialCompetitorFiltersSnapshot.current = {
          search: '',
          reseller: 'all',
          country: 'all',
          discountMin: '',
          discountMax: '',
          changedOnly: false,
          favoritesOnly: favoritesOnlyForFetch,
        };
        essentialPromises.push(fetchCompetitorPrices(1, 50, {
          favoritesOnly: favoritesOnlyForFetch,
          sort: 'discount',
          sortDir: 'desc',
        }, signal));
        essentialPromises.push(fetchDashboardStats(signal));
      } else if (activeTab === 'lamptwistProducts') {
        essentialPromises.push(fetchProducts(1, { tab: 'lamptwistProducts' }, signal));
      } else if (activeTab === 'customProducts') {
        essentialPromises.push(fetchProducts(1, { tab: 'customProducts' }, signal));
      }

      await Promise.all(essentialPromises);
      if (!signal.aborted) {
        console.log(`⏱️ [Products] === PAGE LOAD COMPLETE: ${(performance.now() - start).toFixed(0)}ms ===`);
        setInitialLoadDone(true);
      }
    })();

    // Cleanup: abort pending requests when effect re-runs or component unmounts
    return () => {
      console.log('⏱️ [Products] Aborting pending requests');
      abortController.abort();
    };
  }, [user?.brand]);

  // Load data on first visit to each tab (lazy loading)
  useEffect(() => {
    if (!initialLoadDone) return;

    const tabKey = activeTab === 'customProducts' ? 'custom' : activeTab;

    // Check if this tab has already been loaded
    if (tabsLoaded[tabKey]) {
      console.log(`⏱️ [Products] Tab ${activeTab} already loaded, skipping fetch`);
      return;
    }

    // Load data for the tab on first visit
    if (activeTab === 'favorites') {
      if (favoriteProductsList.length === 0) {
        fetchFavoriteProductsList(1, 50, '');
      }
    } else if (activeTab === 'priceMonitoring') {
      if (competitorData.length === 0) {
        fetchCompetitorPrices(1, 50, {
          favoritesOnly: favoritesOnlyFilter,
          sort: sorting.column || 'discount',
          sortDir: sorting.direction || 'desc',
        });
        fetchDashboardStats();
      }
    } else if (activeTab === 'lamptwistProducts') {
      if (products.length === 0) {
        fetchProducts(1, { tab: 'lamptwistProducts' });
      }
    } else if (activeTab === 'customProducts') {
      // Always fetch for custom products as it might be empty legitimately
      if (!tabsLoaded.custom) {
        fetchProducts(1, { tab: 'customProducts' });
      }
    }
  }, [activeTab, initialLoadDone]);

  // Refetch products when URL filter changes (for lamptwistProducts tab)
  useEffect(() => {
    if (!initialLoadDone) return;
    if (activeTab !== 'lamptwistProducts') return;

    fetchProducts(1, { tab: 'lamptwistProducts' });
  }, [urlFilter, initialLoadDone, activeTab]);

  // Refetch products when search or page changes
  const prevSearchRef = React.useRef(debouncedSearchTerm);

  useEffect(() => {
    if (!initialLoadDone) return;

    // Skip priceMonitoring and favorites tabs - they have their own data source
    if (activeTab === 'priceMonitoring' || activeTab === 'favorites') return;

    const searchChanged = prevSearchRef.current !== debouncedSearchTerm;
    prevSearchRef.current = debouncedSearchTerm;

    // Only refetch if search changed
    if (searchChanged) {
      setFilterLoading(true);
      fetchProducts(1, { tab: activeTab, search: debouncedSearchTerm }).finally(() => {
        setFilterLoading(false);
      });
    }
  }, [debouncedSearchTerm, initialLoadDone, activeTab]);

  // Note: Removed lazy loading - data is now loaded at startup for all tabs

  // Helper to get current filter values for competitor prices
  const getCompetitorFilters = () => ({
    search: productFilter,
    reseller: selectedReseller,
    country: selectedCountry,
    discountMin: discountMin,
    discountMax: discountMax,
    changedOnly: onlyChangeVSYesterday,
    date: selectedDate,
    favoritesOnly: favoritesOnlyFilter,
    sort: sorting.column || undefined,
    sortDir: sorting.direction || undefined,
  });

  // Refetch competitor prices when page, items-per-page or sort changes (server-side pagination + sort)
  useEffect(() => {
    if (activeTab === 'priceMonitoring' && initialLoadDone && competitorPagination.count > 0) {
      fetchCompetitorPrices(priceMonitoringPage, priceMonitoringItemsPerPage, getCompetitorFilters());
    }
  }, [priceMonitoringPage, priceMonitoringItemsPerPage, sorting.column, sorting.direction]);

  // Refetch products for lamptwistProducts tab when page, itemsPerPage, urlFilter, or sorting changes
  useEffect(() => {
    if (activeTab === 'lamptwistProducts' && initialLoadDone) {
      setFilterLoading(true);
      fetchProducts(currentPage, { tab: 'lamptwistProducts', limit: itemsPerPage }).finally(() => {
        setFilterLoading(false);
      });
    }
  }, [currentPage, itemsPerPage, urlFilter, sorting]);

  // Refetch products for customProducts tab when page, itemsPerPage, or sorting changes
  useEffect(() => {
    if (activeTab === 'customProducts' && initialLoadDone) {
      setFilterLoading(true);
      fetchProducts(currentPage, { tab: 'customProducts', limit: itemsPerPage }).finally(() => {
        setFilterLoading(false);
      });
    }
  }, [currentPage, itemsPerPage, sorting]);

  // Refetch competitor prices when filters change (reset to page 1).
  // initialLoadDone is in the deps so the effect *always* fires once after
  // initial load completes — even when no filter dep changed in the same
  // render batch (e.g. when React splits setSelectedDate and setInitialLoadDone
  // across ticks, or when the backend returned no dates to set). That first
  // post-load fire flips the guard from false to true, so the user's next
  // real filter toggle (e.g. unchecking "Favorites only") is no longer
  // silently swallowed.
  useEffect(() => {
    if (activeTab === 'priceMonitoring' && initialLoadDone) {
      const current = getCompetitorFilters();
      // Skip the first trigger - initial load is handled at startup
      if (!competitorFiltersInitialized.current) {
        competitorFiltersInitialized.current = true;
        // If the user toggled a filter *during* the initial load, the effect
        // fired with initialLoadDone=false and was early-returned, dropping
        // their interaction. Compare the current filter values to the
        // snapshot of what the initial fetch went out with (excluding date,
        // which is populated by the backend response) and refetch when they
        // diverge.
        const snapshot = initialCompetitorFiltersSnapshot.current;
        const drifted = snapshot && (
          current.search !== snapshot.search ||
          current.reseller !== snapshot.reseller ||
          current.country !== snapshot.country ||
          current.discountMin !== snapshot.discountMin ||
          current.discountMax !== snapshot.discountMax ||
          current.changedOnly !== snapshot.changedOnly ||
          current.favoritesOnly !== snapshot.favoritesOnly
        );
        if (drifted) {
          console.log('⏱️ [Products] Filter toggled during initial load, refetching with current values...');
          setPriceMonitoringPage(1);
          fetchCompetitorPrices(1, priceMonitoringItemsPerPage, current);
        }
        return;
      }
      console.log('⏱️ [Products] Price Monitoring filter change detected, refetching...');
      setPriceMonitoringPage(1);
      fetchCompetitorPrices(1, priceMonitoringItemsPerPage, current);
    }
  }, [productFilter, selectedReseller, selectedCountry, discountMin, discountMax, onlyChangeVSYesterday, selectedDate, favoritesOnlyFilter, initialLoadDone]);

  // Refetch favorites products when page changes (server-side pagination)
  useEffect(() => {
    if (activeTab === 'favorites' && initialLoadDone && favoritesPagination.count > 0) {
      fetchFavoriteProductsList(favoritesPage, favoritesItemsPerPage, debouncedFavoritesSearch);
    }
  }, [favoritesPage, favoritesItemsPerPage]);

  // Refetch favorites products when search changes (reset to page 1)
  const favoritesSearchInitialized = React.useRef(false);
  useEffect(() => {
    if (activeTab === 'favorites' && initialLoadDone) {
      // Skip the first trigger - initial load is handled at startup
      if (!favoritesSearchInitialized.current) {
        favoritesSearchInitialized.current = true;
        return;
      }
      console.log('⏱️ [Products] Favorites search change detected, refetching...');
      setFavoritesPage(1);
      fetchFavoriteProductsList(1, favoritesItemsPerPage, debouncedFavoritesSearch);
    }
  }, [debouncedFavoritesSearch]);

  // Refetch dashboard stats when date changes
  const dashboardStatsDateInitialized = React.useRef(false);
  useEffect(() => {
    if (activeTab === 'priceMonitoring' && initialLoadDone && selectedDate) {
      // Skip the first trigger - initial load is handled at startup
      if (!dashboardStatsDateInitialized.current) {
        dashboardStatsDateInitialized.current = true;
        return;
      }
      console.log('⏱️ [Products] Date changed, refetching dashboard stats...');
      fetchDashboardStats(null, selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const resellerParam = searchParams.get('reseller');

    if (tabParam) {
      setActiveTab((prev) => (prev === tabParam ? prev : tabParam));
    }

    if (resellerParam) {
      setSelectedReseller((prev) => (prev === resellerParam ? prev : resellerParam));
    } else {
      setSelectedReseller((prev) => (prev === 'all' ? prev : 'all'));
    }

    if (!filtersInitialized) {
      setFiltersInitialized(true);
    }
  }, [searchParams, filtersInitialized]);

  useEffect(() => {
    if (!filtersInitialized) return;

    const params = new URLSearchParams();
    if (activeTab) {
      params.set('tab', activeTab);
    }
    if (selectedReseller && selectedReseller !== 'all') {
      params.set('reseller', selectedReseller);
    }

    const newSearch = params.toString();
    const currentSearch = searchParams.toString();

    if (newSearch !== currentSearch) {
      setSearchParams(params, { replace: true });
    }
  }, [activeTab, selectedReseller, filtersInitialized, searchParams, setSearchParams]);


  const fetchDashboardStats = async (signal, date = null) => {
    const start = performance.now();
    try {
      setStatsLoading(true);
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      const url = params.toString() ? `/api/dashboard/stats/?${params}` : '/api/dashboard/stats/';
      const response = await axiosInstance.get(url, { signal });
      setDashboardStats(response.data);
      console.log(`⏱️ [Products] fetchDashboardStats: ${(performance.now() - start).toFixed(0)}ms`);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // State for server-side pagination of competitor prices
  const [competitorPagination, setCompetitorPagination] = useState({
    count: 0,
    page: 1,
    page_size: 50,
    total_pages: 1
  });

  const fetchCompetitorPrices = async (page = 1, pageSize = 50, filters = {}, signal) => {
    const start = performance.now();
    try {
      setPriceMonitoringLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      // Add filter parameters
      if (filters.search) params.set('search', filters.search);
      if (filters.reseller && filters.reseller !== 'all') params.set('reseller', filters.reseller);
      if (filters.country && filters.country !== 'all') params.set('country', filters.country);
      if (filters.discountMin) params.set('discount_min', filters.discountMin);
      if (filters.discountMax) params.set('discount_max', filters.discountMax);
      if (filters.changedOnly) params.set('changed_only', 'true');
      if (filters.date) params.set('date', filters.date);
      if (filters.favoritesOnly) params.set('favorites_only', 'true');
      if (filters.sort && filters.sortDir) {
        params.set('sort', filters.sort);
        params.set('sort_dir', filters.sortDir);
      }

      const response = await axiosInstance.get(`/api/competitor-prices/?${params}`, { signal });

      setCompetitorData(response.data.data || []);
      setAvailableDates(response.data.dates || []);
      setCompetitorPagination({
        count: response.data.count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 1
      });

      // Update unique resellers from API response only when no reseller filter is applied
      // This ensures we keep the full list even when filtering by a specific reseller
      if (response.data.unique_resellers && (!filters.reseller || filters.reseller === 'all')) {
        setUniqueResellersFromApi(response.data.unique_resellers);
      }

      // Only set selectedDate on initial load (when it's empty)
      if (response.data.dates && response.data.dates.length > 0 && !selectedDate) {
        setSelectedDate(response.data.dates[0]);
      }
      // Mark priceMonitoring tab as loaded
      setTabsLoaded(prev => ({ ...prev, priceMonitoring: true }));
      console.log(`⏱️ [Products] fetchCompetitorPrices: ${(performance.now() - start).toFixed(0)}ms | ${response.data.data?.length || 0} items (page ${page}/${response.data.total_pages}, total: ${response.data.count})`);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching competitor prices:', error);
    } finally {
      setPriceMonitoringLoading(false);
    }
  };

  const fetchAvailableResellers = async (signal) => {
    const start = performance.now();
    try {
      const response = await axiosInstance.get('/api/resellers/available/', { signal });
      setAvailableResellers(response.data.resellers || []);
      console.log(`⏱️ [Products] fetchAvailableResellers: ${(performance.now() - start).toFixed(0)}ms`);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Error fetching available resellers:', err);
    }
  };

  const fetchFavorites = async (signal) => {
    const start = performance.now();
    try {
      const response = await axiosInstance.get('/api/favorites/', { signal });
      const data = {
        shopify_ids: response.data.shopify_ids || [],
        eans: response.data.eans || []
      };
      setFavoriteIds(data);
      console.log(`⏱️ [Products] fetchFavorites: ${(performance.now() - start).toFixed(0)}ms`);
      return data;
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return null;
      console.error('Error fetching favorites:', err);
      return null;
    }
  };

  // Fetch favorite products list (simplified - products only, no competitor prices)
  const fetchFavoriteProductsList = async (page = 1, pageSize = 50, search = '', signal) => {
    const start = performance.now();
    try {
      setFavoritesLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      if (search) params.set('search', search);

      const response = await axiosInstance.get(`/api/favorites/details/?${params}`, { signal });

      setFavoriteProductsList(response.data.data || []);
      setFavoritesPagination({
        count: response.data.count || 0,
        page: response.data.page || 1,
        page_size: response.data.page_size || pageSize,
        total_pages: response.data.total_pages || 1
      });

      // Mark favorites tab as loaded
      setTabsLoaded(prev => ({ ...prev, favorites: true }));
      console.log(`⏱️ [Products] fetchFavoriteProductsList: ${(performance.now() - start).toFixed(0)}ms | ${response.data.data?.length || 0} items (page ${page}/${response.data.total_pages}, total: ${response.data.count})`);
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching favorite products list:', error);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleToggleFavorite = useCallback(async (product, e) => {
    if (e) {
      e.stopPropagation();
    }

    const isCustom = product.is_custom;
    const shopifyId = product.shopify_id;
    const ean = product.v_ean;

    // Check if already favorited
    const isCurrentlyFavorite = isCustom
      ? favoriteIds.eans.includes(ean)
      : favoriteIds.shopify_ids.includes(shopifyId);

    try {
      if (isCurrentlyFavorite) {
        // Remove from favorites
        const payload = isCustom ? { ean } : { shopify_id: shopifyId };
        await axiosInstance.post('/api/favorites/remove/', payload);
        toast({
          description: t('favorites.removed'),
        });
      } else {
        // Add to favorites
        const payload = isCustom ? { ean } : { shopify_id: shopifyId };
        await axiosInstance.post('/api/favorites/add/', payload);
        toast({
          description: t('favorites.added'),
        });
      }
      // Refresh favorites list
      await fetchFavorites();

      // Refresh favorites tab data
      // Reset to page 1 and fetch fresh data
      setFavoritesPage(1);
      fetchFavoriteProductsList(1, favoritesItemsPerPage, debouncedFavoritesSearch);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: err.response?.data?.error || t('favorites.error'),
      });
    }
  }, [favoriteIds, toast, t, favoritesItemsPerPage, debouncedFavoritesSearch]);

  const isFavorite = useCallback((product) => {
    if (product.is_custom) {
      return favoriteIds.eans.includes(product.v_ean);
    }
    return favoriteIds.shopify_ids.includes(product.shopify_id);
  }, [favoriteIds]);

  const toggleRowExpansion = async (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
      // Fetch URLs if not already loaded
      if (!productUrlsMap[rowId]) {
        // Check if it's a custom product
        const isCustom = rowId.toString().startsWith('custom_');
        if (isCustom) {
          // For custom products, fetch URLs by EAN
          const customId = rowId.replace('custom_', '');
          const product = products.find(p => p.custom_product_id === parseInt(customId));
          if (product && product.v_ean) {
            await fetchCustomProductUrls(rowId, product.v_ean);
          }
        } else {
          // For regular products, use shopify_id
          await fetchProductUrls(rowId);
        }
      }
    }
    setExpandedRows(newExpanded);
  };

  const fetchProductUrls = async (shopifyId) => {
    try {
      setLoadingUrls(prev => ({ ...prev, [shopifyId]: true }));
      const response = await axiosInstance.get(`/api/products/${shopifyId}/urls/`);
      setProductUrlsMap(prev => ({
        ...prev,
        [shopifyId]: response.data.urls || { twistscrape: [], brand_custom: [] }
      }));
    } catch (err) {
      console.error('Error fetching product URLs:', err);
    } finally {
      setLoadingUrls(prev => ({ ...prev, [shopifyId]: false }));
    }
  };

  const fetchCustomProductUrls = async (rowId, ean) => {
    try {
      setLoadingUrls(prev => ({ ...prev, [rowId]: true }));
      // Fetch only custom URLs for this EAN (no twistscrape URLs for custom products)
      const response = await axiosInstance.get(`/api/products/custom-urls/?ean=${ean}`);
      setProductUrlsMap(prev => ({
        ...prev,
        [rowId]: {
          twistscrape: [],
          brand_custom: response.data.urls || []
        }
      }));
    } catch (err) {
      console.error('Error fetching custom product URLs:', err);
      // If API fails, just show empty
      setProductUrlsMap(prev => ({
        ...prev,
        [rowId]: { twistscrape: [], brand_custom: [] }
      }));
    } finally {
      setLoadingUrls(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const handleDeleteUrl = async (urlId, rowId, product, urlItem) => {
    // For custom products, no confirmation needed
    const isCustom = product.is_custom;
    
    if (!isCustom) {
      // Only ask for confirmation for regular products
      if (!window.confirm(t('urls.confirmDelete'))) {
        return;
      }
    }

    try {
      const endpoint = isCustom
        ? `/api/products/custom-urls/${urlId}/delete/`
        : `/api/products/${product.shopify_id}/urls/${urlId}/delete/`;

      await axiosInstance.delete(endpoint);

      toast({
        title: t('success'),
        description: t('urls.deleteSuccess'),
      });

      // Refresh URLs for this product
      if (isCustom) {
        await fetchCustomProductUrls(rowId, product.v_ean);
      } else {
        await fetchProductUrls(product.shopify_id);
      }

      // Reload products list to update URLs count
      await fetchProducts(currentPage, { tab: activeTab, search: debouncedSearchTerm });
    } catch (err) {
      console.error('Error deleting URL:', err);
      toast({
        title: t('error'),
        description: err.response?.data?.error || t('urls.deleteError'),
        variant: 'destructive',
      });
    }
  };

  const handleAddUrlClick = (product) => {
    setSelectedProductForUrl(product);
    setNewUrl({ url: '' }); // EAN will be taken from product
    setUrlError('');
    setShowAddUrlModal(true);
  };

  // Validate if URL belongs to an approved reseller
  const validateUrl = async (url) => {
    if (!url.trim()) return null;
    
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, '');
      
      // Check if hostname matches any available reseller
      // The reseller name might be just the domain name (e.g., 'lampenmaster')
      // while the hostname includes the extension (e.g., 'lampenmaster.be')
      const isValid = availableResellers.some(reseller => {
        const resellerName = (reseller.reseller || reseller.hostname || '').toLowerCase().replace(/^www\./, '');
        // Check exact match or if hostname starts with reseller name followed by a dot
        return hostname === resellerName || hostname.startsWith(resellerName + '.');
      });
      
      return isValid ? 'valid' : 'invalid';
    } catch (e) {
      return 'invalid';
    }
  };

  // Handle URL input change with validation
  const handleUrlInputChange = async (id, value) => {
    setUrlInputs(prevInputs => 
      prevInputs.map(input => 
        input.id === id ? { ...input, url: value, status: 'validating' } : input
      )
    );
    
    // Validate URL
    const status = await validateUrl(value);
    setUrlInputs(prevInputs => 
      prevInputs.map(input => 
        input.id === id ? { ...input, status } : input
      )
    );
  };

  // Add new URL input field
  const addUrlInput = () => {
    const newId = Math.max(...urlInputs.map(u => u.id), 0) + 1;
    setUrlInputs([...urlInputs, { id: newId, url: '', status: null }]);
  };

  // Remove URL input field
  const removeUrlInput = (id) => {
    if (urlInputs.length > 1) {
      setUrlInputs(urlInputs.filter(input => input.id !== id));
    }
  };

  const handleAddUrl = async () => {
    setUrlError('');
    
    // Filter valid URLs
    const validUrls = urlInputs.filter(input => input.url.trim() && input.status === 'valid');
    
    if (validUrls.length === 0) {
      setUrlError(t('urls.noValidUrls'));
      return;
    }

    if (!selectedProductForUrl) return;

    // Get EAN from product
    const ean = selectedProductForUrl.v_ean || '';
    if (!ean.trim()) {
      setUrlError(t('urls.eanRequired'));
      return;
    }

    try {
      setAddingUrl(true);
      
      // Add all valid URLs
      const results = await Promise.allSettled(
        validUrls.map(input => 
          axiosInstance.post(`/api/products/${selectedProductForUrl.shopify_id}/urls/add/`, {
            url: input.url.trim(),
            ean: ean.trim()
          })
        )
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast({
          title: t('success'),
          description: `${successCount} URL(s) added successfully${failCount > 0 ? `, ${failCount} failed` : ''}`,
        });
        // Refresh URLs for this product
        await fetchProductUrls(selectedProductForUrl.shopify_id);
        // Reload products list to update tracked status
        await fetchProducts(currentPage, { tab: activeTab, search: debouncedSearchTerm });
        // Reset form and close modal
        setUrlInputs([{ id: 1, url: '', status: null }]);
        setNewUrl({ url: '' });
        setUrlError('');
        setSelectedProductForUrl(null);
        setShowAddUrlModal(false);
      } else {
        // Extract error message from first failed request
        const firstError = results.find(r => r.status === 'rejected');
        const errorMessage = firstError?.reason?.response?.data?.error || t('urls.addError');
        setUrlError(errorMessage);
      }
    } catch (err) {
      console.error('Error adding URLs:', err);
      setUrlError(err.response?.data?.error || t('urls.addError'));
    } finally {
      setAddingUrl(false);
    }
  };

  const handleAddCustomProduct = async () => {
    setCustomProductError('');
    
    // Validate required fields
    if (!newCustomProduct.name.trim()) {
      setCustomProductError(t('customProduct.nameRequired'));
      return;
    }
    
    if (!newCustomProduct.ean.trim()) {
      setCustomProductError(t('customProduct.eanRequired'));
      return;
    }
    
    if (newCustomProduct.ean.trim().length < 8) {
      setCustomProductError(t('customProduct.eanTooShort'));
      return;
    }
    
    try {
      setAddingCustomProduct(true);
      const response = await axiosInstance.post('/api/custom-products/create/', {
        name: newCustomProduct.name.trim(),
        ean: newCustomProduct.ean.trim(),
        rrp_incl_vat: newCustomProduct.rrp_incl_vat || null,
        vendor_code: newCustomProduct.vendor_code.trim(),
        urls: newCustomProduct.urls
      });
      
      // Reset form and close modal
      setNewCustomProduct({
        name: '',
        variant_name: '',
        ean: '',
        rrp_incl_vat: '',
        vendor_code: '',
        urls: []
      });
      setCustomProductError('');
      setShowAddCustomProductModal(false);

      // Refetch custom products data and switch to tab
      setTabsLoaded(prev => ({ ...prev, custom: false }));
      setActiveTab('customProducts');
      fetchProducts(1, { tab: 'customProducts' });
    } catch (err) {
      console.error('Error adding custom product:', err);
      setCustomProductError(err.response?.data?.error || t('customProduct.addError'));
    } finally {
      setAddingCustomProduct(false);
    }
  };

  const handleDeleteCustomProduct = async () => {
    if (!productToDelete) return;

    try {
      setDeletingProduct(true);
      await axiosInstance.delete(`/api/custom-products/${productToDelete.custom_product_id}/delete/`);
      
      toast({
        title: t('success'),
        description: t('customProduct.deleteSuccess'),
      });

      // Refresh products list
      await fetchProducts(currentPage, { tab: activeTab, search: debouncedSearchTerm });

      // Close modal and reset
      setShowDeleteConfirmModal(false);
      setProductToDelete(null);
    } catch (err) {
      console.error('Error deleting custom product:', err);
      toast({
        title: t('error'),
        description: err.response?.data?.error || t('customProduct.deleteError'),
        variant: 'destructive',
      });
    } finally {
      setDeletingProduct(false);
    }
  };
  
  const handleAddUrlToCustomProduct = () => {
    const newUrls = [...newCustomProduct.urls, { url: '' }];
    setNewCustomProduct({ ...newCustomProduct, urls: newUrls });
  };
  
  const handleRemoveUrlFromCustomProduct = (index) => {
    const newUrls = newCustomProduct.urls.filter((_, i) => i !== index);
    setNewCustomProduct({ ...newCustomProduct, urls: newUrls });
  };
  
  const handleUrlChangeForCustomProduct = (index, value) => {
    const newUrls = [...newCustomProduct.urls];
    newUrls[index] = { url: value };
    setNewCustomProduct({ ...newCustomProduct, urls: newUrls });
  };


  // Server-side pagination: fetch only the current page
  const fetchProducts = async (page = 1, options = {}, signal) => {
    const perfStart = performance.now();
    console.log(`⏱️ [Products] START fetchProducts page=${page} tab=${options.tab} search=${options.search || ''}`);

    try {
      setLoading(true);

      // Build query parameters for server-side filtering
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
      });

      // Add search filter
      if (options.search) {
        params.set('search', options.search);
      }

      // Add tab-specific filters
      if (options.tab === 'lamptwistProducts') {
        params.set('url_status', urlFilter); // 'all', 'with_urls', 'without_urls'
      } else if (options.tab === 'favorites') {
        params.set('favorites', 'true');
      } else if (options.tab === 'customProducts') {
        params.set('custom_only', 'true');
      }

      // Add sorting if active
      if (sorting.column && sorting.direction) {
        params.set('ordering', sorting.direction === 'desc' ? `-${sorting.column}` : sorting.column);
      }

      const apiStart = performance.now();
      const response = await axiosInstance.get(`/api/products/?${params}`, { signal });
      const apiEnd = performance.now();
      console.log(`⏱️ [Products] API response: ${(apiEnd - apiStart).toFixed(0)}ms | ${response.data.results?.length || 0} items | total=${response.data.count}`);

      const renderStart = performance.now();
      const results = response.data.results || [];
      const paginationData = {
        count: response.data.count,
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages
      };

      // Store data in the appropriate state based on tab
      if (options.tab === 'lamptwistProducts') {
        setProducts(results);
        setPagination(paginationData);
      } else if (options.tab === 'customProducts') {
        setCustomProducts(results);
        setCustomProductsPagination(paginationData);
      } else {
        // Default fallback (favorites tab)
        setProducts(results);
        setPagination(paginationData);
      }

      setError(null);
      // Mark tab as loaded (counts are loaded separately by fetchTabCounts on mount)
      if (options.tab) {
        let tabKey = options.tab;
        if (options.tab === 'customProducts') tabKey = 'custom';
        setTabsLoaded(prev => ({ ...prev, [tabKey]: true }));
      }
      console.log(`⏱️ [Products] State updates: ${(performance.now() - renderStart).toFixed(0)}ms`);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Error fetching products:', err);
      setError(t('error'));
    } finally {
      setLoading(false);
      console.log(`⏱️ [Products] TOTAL fetchProducts: ${(performance.now() - perfStart).toFixed(0)}ms`);
    }
  };

  const fetchActiveReports = async (signal) => {
    const start = performance.now();
    try {
      const listStart = performance.now();
      const response = await axiosInstance.get('/api/product-reports/all/', { signal });
      console.log(`⏱️ [Products] fetchActiveReports - list all: ${(performance.now() - listStart).toFixed(0)}ms`);

      // Backend returns the data directly as { "shopify_123": 2, "ean_ABC": 1 }
      // Convert shopify_xxx to just xxx for matching with getRowId
      const reportsMap = {};
      Object.keys(response.data).forEach(key => {
        if (key.startsWith('shopify_')) {
          const shopifyId = key.replace('shopify_', '');
          reportsMap[shopifyId] = response.data[key];
        } else {
          reportsMap[key] = response.data[key];
        }
      });
      setActiveReports(reportsMap);

      // Fetch detailed reports with proposed values
      const reportedValues = {};
      const shopifyIdsWithReports = Object.keys(reportsMap);
      console.log(`⏱️ [Products] fetchActiveReports - ${shopifyIdsWithReports.length} products have reports`);

      const detailStart = performance.now();
      const detailPromises = shopifyIdsWithReports.map(async (shopifyId) => {
        try {
          const detailResponse = await axiosInstance.get(`/api/product-reports/list/?shopify_id=${shopifyId}`, { signal });
          const reports = detailResponse.data;

          if (reports && reports.length > 0) {
            const values = {};
            reports.forEach(report => {
              if (report.issue_type === 'wrong_rrp' && report.proposed_rrp !== null && report.proposed_rrp !== undefined) {
                values.rrp = report.proposed_rrp;
              } else if (report.issue_type === 'wrong_ean' && report.proposed_ean) {
                values.ean = report.proposed_ean;
              } else if (report.issue_type === 'wrong_vendor_code' && report.proposed_vendor_code) {
                values.vendor_code = report.proposed_vendor_code;
              }
            });

            if (Object.keys(values).length > 0) {
              reportedValues[shopifyId] = values;
            }
          }
        } catch (error) {
          if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
          console.error(`Error fetching details for shopify_id ${shopifyId}:`, error);
        }
      });

      await Promise.all(detailPromises);
      console.log(`⏱️ [Products] fetchActiveReports - detail fetches: ${(performance.now() - detailStart).toFixed(0)}ms`);
      setReportedValuesMap(reportedValues);
      console.log(`⏱️ [Products] fetchActiveReports TOTAL: ${(performance.now() - start).toFixed(0)}ms`);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Error fetching active reports:', err);
    }
  };

  const fetchTabCounts = async (signal) => {
    const start = performance.now();
    try {
      const response = await axiosInstance.get('/api/products/tab-counts/', { signal });
      console.log(`⏱️ [Products] fetchTabCounts: ${(performance.now() - start).toFixed(0)}ms`);
      console.log('[Products] Tab counts received from backend:', response.data);

      // Set all counts at once from the response
      setTabCounts({
        favorites: response.data.favorites || 0,
        priceMonitoring: response.data.priceMonitoring || 0,
        lamptwistProducts: response.data.lamptwistProducts || 0,
        tracked: response.data.tracked || 0,
        missingUrls: response.data.missingUrls || 0,
        custom: response.data.customProducts || 0
      });
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Error fetching tab counts:', err);
    }
  };

  // Server-side filtering: products are already filtered by the backend
  // Each tab has its own state now
  const filteredProducts = useMemo(() => {
    // Price Monitoring and Favorites tabs have their own data source
    if (activeTab === 'priceMonitoring' || activeTab === 'favorites') {
      return [];
    }

    // Return the appropriate data based on active tab
    if (activeTab === 'lamptwistProducts') {
      return products;
    } else if (activeTab === 'customProducts') {
      return customProducts;
    }

    return products;
  }, [products, customProducts, activeTab]);

  // Paginated products - all tabs now use server-side pagination
  const paginatedProducts = useMemo(() => {
    // Server handles pagination for all tabs
    return filteredProducts;
  }, [filteredProducts]);

  // Total pages - from server pagination based on active tab
  const totalPages = useMemo(() => {
    if (activeTab === 'lamptwistProducts') {
      return pagination.total_pages || 1;
    } else if (activeTab === 'customProducts') {
      return customProductsPagination.total_pages || 1;
    }
    return pagination.total_pages || 1;
  }, [pagination.total_pages, customProductsPagination.total_pages, activeTab]);

  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, activeTab]);

  // Helper function - uses the hook for reseller images from DB
  const getResellerFavicon = useCallback((hostname) => {
    return getResellerImage(hostname);
  }, [getResellerImage]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setProductFilter('');
    setSelectedReseller('all');
    setSelectedCountry('all');
    setDiscountMin('');
    setDiscountMax('');
    setOnlyChangeVSYesterday(false);
    setFavoritesOnlyFilter(false);
    writeFavoritesOnlyPref(user?.brand, false);
  }, [user?.brand]);

  // Extract domain from URL and remove www prefix
  const extractDomain = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      let hostname = urlObj.hostname;
      // Remove www prefix
      if (hostname.startsWith('www.')) {
        hostname = hostname.substring(4);
      }
      return hostname;
    } catch (e) {
      return null;
    }
  };

  // Helper function to get display value (reported value or original value)
  const getDisplayValue = (row, field) => {
    const shopifyId = row.shopify_id;
    if (shopifyId && reportedValuesMap[shopifyId] && reportedValuesMap[shopifyId][field] !== undefined) {
      return reportedValuesMap[shopifyId][field];
    }
    // Return original value
    if (field === 'rrp') return row.v_compareAtPrice || row.compare_at_price;
    if (field === 'ean') return row.v_ean;
    if (field === 'vendor_code') return row.v_vendor_code;
    return null;
  };

  // Helper to check if a value is reported (different from original)
  const isReportedValue = (row, field) => {
    const shopifyId = row.shopify_id;
    return shopifyId && reportedValuesMap[shopifyId] && reportedValuesMap[shopifyId][field] !== undefined;
  };

  const columnHelper = createColumnHelper();

  // Price Monitoring columns
  const priceMonitoringColumns = React.useMemo(() => [
    columnHelper.accessor('p_title', {
      id: 'product',
      header: ({ column }) => (
        <ColumnHeader column={column}>{tDashboard('table.product')}</ColumnHeader>
      ),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-3">
            {row.v_image_link ? (
              <img
                src={row.v_image_link}
                alt={row.p_title || t('productImage')}
                className="w-10 h-10 object-cover rounded border"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/40';
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-muted rounded border flex items-center justify-center">
                <Package className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate max-w-xs">{row.p_title || '-'}</div>
              {(row.v_variant_title || getDisplayValue(row, 'vendor_code') || getDisplayValue(row, 'ean')) && (
                <div className="text-xs text-muted-foreground truncate max-w-xs">
                  {row.v_variant_title || ''}
                  {row.v_variant_title && (getDisplayValue(row, 'vendor_code') || getDisplayValue(row, 'ean')) ? ' - ' : ''}
                  <span className={isReportedValue(row, 'vendor_code') ? 'font-semibold' : ''}>
                    {getDisplayValue(row, 'vendor_code') || ''}
                  </span>
                  {getDisplayValue(row, 'vendor_code') && getDisplayValue(row, 'ean') ? ' - ' : ''}
                  <span className={isReportedValue(row, 'ean') ? 'font-semibold' : ''}>
                    {getDisplayValue(row, 'ean') || ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('reseller_domain', {
      id: 'reseller',
      header: ({ column }) => (
        <ColumnHeader column={column}>{tDashboard('table.reseller')}</ColumnHeader>
      ),
      cell: (info) => {
        const row = info.row.original;
        const domain = row.reseller_domain || extractDomain(row.url);
        const countryCode = getCountryFlagCode(row.country);
        const url = row.url;
        
        return (
          <div 
            className="flex items-center gap-2 group"
            onClick={(e) => {
              e.stopPropagation();
              if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {countryCode ? (
              <ReactCountryFlag
                countryCode={countryCode}
                svg
                style={{
                  width: '1em',
                  height: '1em',
                }}
              />
            ) : (
              <Globe className="h-4 w-4" />
            )}
            {getResellerFavicon(domain) && (
              <img
                src={getResellerFavicon(domain)}
                alt={t('resellerFavicon')}
                className="w-4 h-4 rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span className="text-sm font-medium cursor-pointer hover:text-primary hover:underline transition-colors">{domain || t('unknownReseller')}</span>
            {url && (
              <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            )}
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('v_compareAtPrice', {
      id: 'rrp',
      header: ({ column }) => (
        <div className="text-right">
          <ColumnHeader column={column}>{tDashboard('table.rrp')}</ColumnHeader>
        </div>
      ),
      cell: (info) => {
        const row = info.row.original;
        const price = getDisplayValue(row, 'rrp');
        const isReported = isReportedValue(row, 'rrp');
        const isCompact = activeTab === 'priceMonitoring';
        return (
          <div className={`text-right ${isCompact ? 'text-xs' : 'text-sm'} ${isReported ? 'font-semibold' : ''}`}>
            {price ? `€ ${parseFloat(price).toFixed(2)}` : '-'}
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('price_reseller', {
      id: 'price',
      header: ({ column }) => (
        <div className="text-right">
          <ColumnHeader column={column}>{tDashboard('table.price')}</ColumnHeader>
        </div>
      ),
      cell: (info) => {
        const row = info.row.original;
        const price = row.price_reseller || row.price;
        const isCompact = activeTab === 'priceMonitoring';
        return (
          <div className={`text-right ${isCompact ? 'text-xs' : 'text-sm'} font-medium`}>
            {price ? `€ ${parseFloat(price).toFixed(2)}` : '-'}
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('discount', {
      id: 'discount',
      header: ({ column }) => (
        <div className="text-center">
          <ColumnHeader column={column}>{tDashboard('table.discount')}</ColumnHeader>
        </div>
      ),
      cell: (info) => {
        const discount = info.getValue();
        const isCompact = activeTab === 'priceMonitoring';
        if (discount === null || discount === undefined) {
          return <div className={`text-center ${isCompact ? 'text-xs' : 'text-sm'}`}>-</div>;
        }
        const value = parseFloat(discount);
        return (
          <div className={`text-center ${isCompact ? 'text-xs' : 'text-sm'} font-medium ${
            value > 0 ? 'text-red-600' : value < 0 ? 'text-green-600' : ''
          }`}>
            {value < 0 ? '+' : ''}{Math.abs(value).toFixed(2)}%
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('evolution_yesterday', {
      id: 'vs_yesterday',
      header: ({ column }) => (
        <div className="text-center">
          <ColumnHeader column={column}>{tDashboard('table.vsYesterday')}</ColumnHeader>
        </div>
      ),
      cell: (info) => {
        const value = info.getValue();
        const isCompact = activeTab === 'priceMonitoring';
        
        if (value === null || value === undefined) {
          return <div className={`text-center ${isCompact ? 'text-xs' : 'text-sm'}`}>-</div>;
        }
        const numValue = parseFloat(value);
        
        if (numValue === 0) {
          return (
            <div className="text-center">
              <div className={`${isCompact ? 'text-xs' : 'text-sm'} text-muted-foreground`}>0%</div>
            </div>
          );
        }
        
        // Positive value means discount increased (yesterday discount was higher)
        // Negative value means discount decreased (yesterday discount was lower)
        // For display: if discount increased (positive), show in red with + and up arrow
        // If discount decreased (negative), show in green with - and down arrow
        const isDiscountIncrease = numValue < 0; // Negative evolution means discount increased
        const displayValue = Math.abs(numValue);
        
        return (
          <div className="text-center">
            <div className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium ${
              isDiscountIncrease ? 'text-red-600' : 'text-green-600'
            }`}>
              {isDiscountIncrease ? '+' : '-'}{displayValue.toFixed(2)}%
            </div>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor('evolution_last_week', {
      id: 'vs_last_week',
      header: ({ column }) => (
        <div className="text-center">
          <ColumnHeader column={column}>{tDashboard('table.vsLastWeek')}</ColumnHeader>
        </div>
      ),
      cell: (info) => {
        const value = info.getValue();
        
        if (value === null || value === undefined) {
          return <div className="text-center text-xs">-</div>;
        }
        const numValue = parseFloat(value);
        
        if (numValue === 0) {
          return (
            <div className="text-center">
              <div className="text-xs text-muted-foreground">0%</div>
            </div>
          );
        }
        
        // Positive value means discount increased (last week discount was higher)
        // Negative value means discount decreased (last week discount was lower)
        // For display: if discount increased (positive), show in red with + and up arrow
        // If discount decreased (negative), show in green with - and down arrow
        const isDiscountIncrease = numValue < 0; // Negative evolution means discount increased
        const displayValue = Math.abs(numValue);
        
        return (
          <div className="text-center">
            <div className={`text-xs font-medium ${
              isDiscountIncrease ? 'text-red-600' : 'text-green-600'
            }`}>
              {isDiscountIncrease ? '+' : '-'}{displayValue.toFixed(2)}%
            </div>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => t('table.actions'),
      cell: (info) => {
        const row = info.row.original;
        const rowId = getRowId(row);
        // Check if there are active reports for this product
        let reportKey = rowId;
        if (row.is_custom && row.v_ean) {
          reportKey = `ean_${row.v_ean}`;
        }
        const hasReport = reportKey && activeReports[reportKey] > 0;
        const isProductFavorite = isFavorite(row);

        return (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {/* Favorite toggle button */}
              <button
                onClick={(e) => handleToggleFavorite(row, e)}
                className="p-1 hover:bg-muted rounded"
                title={isProductFavorite ? t('favorites.remove') : t('favorites.add')}
              >
                <Star
                  className={`h-4 w-4 ${isProductFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
                />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openReportModal(row, 'priceMonitoring');
                }}
                className="p-1 hover:bg-muted rounded"
                title={t('report.tooltip')}
              >
                <Flag className={`h-4 w-4 ${hasReport ? 'text-red-600' : 'text-muted-foreground'}`} />
              </button>
            </div>
        );
      },
      enableSorting: false,
      size: 90,
      minSize: 80,
    }),
  ], [tDashboard, activeReports, t, reportedValuesMap, isFavorite, handleToggleFavorite]);

  // Competitor data is now filtered server-side, just return it directly
  const filteredCompetitorData = useMemo(() => {
    if (activeTab !== 'priceMonitoring') return [];
    // Data is already filtered by the backend - no client-side filtering needed
    return competitorData;
  }, [competitorData, activeTab]);

  // With server-side pagination, use data directly (already paginated from server)
  const paginatedCompetitorData = useMemo(() => {
    if (activeTab !== 'priceMonitoring') return [];
    return filteredCompetitorData;
  }, [filteredCompetitorData, activeTab]);

  // Use server pagination info for total pages
  const priceMonitoringTotalPages = competitorPagination.total_pages;

  // Use server pagination info for favorites total pages
  const favoritesTotalPages = favoritesPagination.total_pages;

  // Get unique resellers - prefer API response (all resellers) over page data
  const uniqueResellers = useMemo(() => {
    // Use resellers from API if available (complete list from all results)
    if (uniqueResellersFromApi.length > 0) {
      return uniqueResellersFromApi;
    }
    // Fallback to extracting from current page data
    const resellers = new Set();
    competitorData.forEach(item => {
      const domain = item.reseller_domain || extractDomain(item.url);
      if (domain) {
        resellers.add(domain);
      }
    });
    return Array.from(resellers).sort();
  }, [uniqueResellersFromApi, competitorData]);

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.display({
        id: 'product',
        header: ({ column }) => (
          <ColumnHeader column={column}>{t('table.product')}</ColumnHeader>
        ),
        meta: {
          className: 'text-left',
        },
        cell: (info) => {
          const row = info.row.original;
          const imageLink = row.v_image_link;
          const title = row.p_title || t('noTitle');
          const variantTitle = row.v_variant_title;
          const vendorCode = getDisplayValue(row, 'vendor_code');
          const ean = getDisplayValue(row, 'ean');
          const isVendorCodeReported = isReportedValue(row, 'vendor_code');
          const isEanReported = isReportedValue(row, 'ean');

          const isCompact = activeTab === 'priceMonitoring';
          return (
            <div className={`flex items-center ${isCompact ? 'gap-2' : 'gap-3'}`}>
              {imageLink ? (
                <img
                  src={imageLink}
                  alt={title}
                  className={`${isCompact ? 'w-8 h-8' : 'w-10 h-10'} object-cover rounded border flex-shrink-0`}
                  onError={(e) => {
                    e.target.src = isCompact ? 'https://via.placeholder.com/32' : 'https://via.placeholder.com/40';
                  }}
                />
              ) : (
                <div className={`${isCompact ? 'w-8 h-8' : 'w-10 h-10'} bg-muted rounded border flex items-center justify-center text-muted-foreground flex-shrink-0`}>
                  <Package className={isCompact ? 'h-4 w-4' : 'h-5 w-5'} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className={`flex items-center ${isCompact ? 'gap-1.5' : 'gap-2'}`}>
                  <div className={`font-medium ${isCompact ? 'text-xs' : 'text-sm'} truncate max-w-xs`}>{title}</div>
                  {row.is_custom && activeTab !== 'customProducts' && (
                    <span
                      className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"
                      title={t('customProductBadge')}
                    >
                      {t('customBadge')}
                    </span>
                  )}
                </div>
                {(variantTitle || vendorCode || ean) && (
                  <div className={`text-[12px] text-muted-foreground truncate max-w-xs`}>
                    {variantTitle || ''}
                    {variantTitle && (vendorCode || ean) ? ' - ' : ''}
                    <span className={isVendorCodeReported ? 'font-semibold' : ''}>
                      {vendorCode || ''}
                    </span>
                    {vendorCode && ean ? ' - ' : ''}
                    <span className={isEanReported ? 'font-semibold' : ''}>
                      {ean || ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor('v_compareAtPrice', {
        header: ({ column }) => (
          <ColumnHeader column={column}>{t('table.rrp')}</ColumnHeader>
        ),
        meta: {
          className: 'text-right',
        },
        cell: (info) => {
          const row = info.row.original;
          const price = getDisplayValue(row, 'rrp');
          const isReported = isReportedValue(row, 'rrp');
          const isCompact = activeTab === 'priceMonitoring';
          return (
            <div className={`text-right ${isCompact ? 'text-xs' : 'text-sm'} ${isReported ? 'font-semibold' : ''}`}>
              {price ? `€ ${parseFloat(price).toFixed(2)}` : '-'}
            </div>
          );
        },
        enableSorting: true,
        size: 120,
        minSize: 100,
      }),
    ];

    // Add URLs column only for lamptwistProducts and customProducts tabs
    if (activeTab === 'lamptwistProducts' || activeTab === 'customProducts') {
      baseColumns.push(
        columnHelper.accessor('urls_count', {
          id: 'urls_count',
          header: ({ column }) => (
            <ColumnHeader column={column}>{t('table.urls')}</ColumnHeader>
          ),
          meta: {
            className: 'text-center',
          },
          cell: (info) => {
            const row = info.row.original;
            const rowId = row.is_custom ? `custom_${row.custom_product_id}` : row.shopify_id;
            const urlsCount = info.getValue() || 0;
            const isExpanded = expandedRows.has(rowId);
            const isCompact = activeTab === 'priceMonitoring';

            if (urlsCount > 0) {
              return (
                <div className="flex items-center justify-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRowExpansion(rowId);
                    }}
                    className={`flex items-center gap-1 ${isCompact ? 'text-xs' : 'text-sm'} text-primary hover:underline cursor-pointer`}
                    title={t('table.showUrls')}
                  >
                    <span>{urlsCount}</span>
                    {isExpanded ? (
                      <ChevronDown className={isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                    ) : (
                      <ChevronRight className={isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                    )}
                  </button>
                </div>
              );
            }

            return (
              <div className={`text-center ${isCompact ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
                -
              </div>
            );
          },
          enableSorting: true,
          size: 80,
          minSize: 60,
        })
      );
    }

    // Actions column for tracked/missing/custom
    baseColumns.push(
      columnHelper.display({
        id: 'actions',
        header: () => t('table.actions'),
        meta: {
          className: 'text-center',
        },
        cell: (info) => {
          const row = info.row.original;
          const isCompact = activeTab === 'priceMonitoring';
          const rowId = getRowId(row);
          // Check if there are active reports for this product
          let reportKey = rowId;
          if (row.is_custom && row.v_ean) {
            reportKey = `ean_${row.v_ean}`;
          }
          const hasReport = reportKey && activeReports[reportKey] > 0;

          const isProductFavorite = isFavorite(row);

          return (
            <div className={`flex items-center justify-center ${isCompact ? 'gap-1' : 'gap-2'}`} onClick={(e) => e.stopPropagation()}>
              {/* Favorite toggle button */}
              <button
                onClick={(e) => handleToggleFavorite(row, e)}
                className={isCompact ? 'p-0.5 hover:bg-muted rounded' : 'p-1 hover:bg-muted rounded'}
                title={isProductFavorite ? t('favorites.remove') : t('favorites.add')}
              >
                <Star
                  className={`${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${isProductFavorite ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
                />
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddUrlClick(row);
                }}
                className={isCompact ? 'p-0.5 hover:bg-muted rounded' : 'p-1 hover:bg-muted rounded'}
                title={t('urls.addUrl')}
              >
                <CirclePlus className={`${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-muted-foreground`} />
              </button>

              {activeTab !== 'customProducts' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openReportModal(row, activeTab);
                  }}
                  className={isCompact ? 'p-0.5 hover:bg-muted rounded' : 'p-1 hover:bg-muted rounded'}
                  title={t('report.tooltip')}
                >
                  <Flag className={`${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} ${hasReport ? 'text-red-600' : 'text-muted-foreground'}`} />
                </button>
              )}

              {row.is_custom && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setProductToDelete(row);
                    setShowDeleteConfirmModal(true);
                  }}
                  className={isCompact ? 'p-0.5 hover:bg-muted rounded hover:text-red-600' : 'p-1 hover:bg-muted rounded hover:text-red-600'}
                  title={t('table.delete')}
                >
                  <Trash2 className={`${isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-muted-foreground`} />
                </button>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 120,
        minSize: 100,
      })
    );

    return baseColumns;
  }, [activeTab, expandedRows, activeReports, t, reportedValuesMap, isFavorite, handleToggleFavorite]);

  return (
    <Layout>
      <PageHeader 
        title={<h1 className="text-3xl font-bold">{t('title')}</h1>}
        rightContent={
          <Button onClick={() => setShowAddCustomProductModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('addNewProduct')}
          </Button>
        }
      />
      <div className="space-y-6">
            {loading && !Object.values(tabsLoaded).some(v => v) ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="border rounded-lg p-8 text-center">
                <p className="text-red-500">{error}</p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto">
                  <TabsTrigger value="priceMonitoring" className="whitespace-normal text-center leading-tight h-auto py-2">
                    {t('tabs.priceMonitoring')}
                  </TabsTrigger>
                  <TabsTrigger value="lamptwistProducts" className="whitespace-normal text-center leading-tight h-auto py-2">
                    {t('tabs.lamptwistProducts')}
                  </TabsTrigger>
                  <TabsTrigger value="customProducts" className="whitespace-normal text-center leading-tight h-auto py-2">
                    {t('tabs.customProducts')}
                  </TabsTrigger>
                  <TabsTrigger value="favorites" className="whitespace-normal text-center leading-tight h-auto py-2">
                    <Star className="h-4 w-4 mr-1 inline" />
                    {t('tabs.favorites')}
                  </TabsTrigger>
                </TabsList>

            {/* Favorites Tab Content - Simplified product list */}
            <TabsContent value="favorites" className="space-y-6">
              {/* Search bar for favorites */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  {favoritesLoading ? (
                    <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
                  ) : (
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  )}
                  <Input
                    type="text"
                    placeholder={t('search.placeholder')}
                    value={favoritesSearch}
                    onChange={(e) => setFavoritesSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Favorites Table */}
              {favoritesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : favoritesPagination.count === 0 && favoriteIds.shopify_ids.length === 0 && favoriteIds.eans.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('favorites.empty')}</p>
                    <p className="text-sm text-muted-foreground mt-2">{t('favorites.emptyHint')}</p>
                  </CardContent>
                </Card>
              ) : favoritesPagination.count === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">{t('favorites.noResults')}</p>
                    <p className="text-sm text-muted-foreground mt-2">{t('favorites.noResultsHint')}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">
                      <Star className="h-5 w-5 inline mr-2 text-yellow-500" />
                      {t('tabs.favorites')} ({favoritesPagination.count})
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('favorites.simpleDescription')}
                    </p>
                  </CardHeader>
                  <CardContent>
                    {/* Simple table for favorite products */}
                    <div className="rounded-md border">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="h-10 px-4 align-middle text-left text-sm font-medium text-muted-foreground">{t('table.product')}</th>
                            <th className="h-10 px-4 align-middle text-left text-sm font-medium text-muted-foreground">{t('table.ean')}</th>
                            <th className="h-10 px-4 align-middle text-left text-sm font-medium text-muted-foreground">{t('table.vendorCode')}</th>
                            <th className="h-10 px-4 align-middle text-right text-sm font-medium text-muted-foreground">{t('table.rrp')}</th>
                            <th className="h-10 px-4 align-middle text-center text-sm font-medium text-muted-foreground">{t('table.urls')}</th>
                            <th className="h-10 px-4 align-middle text-center text-sm font-medium text-muted-foreground w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {favoriteProductsList.map((product, index) => {
                            const rowId = product.is_custom ? `custom_${product.custom_product_id}` : product.shopify_id;
                            const isExpanded = expandedRows.has(rowId);
                            const urls = productUrlsMap[rowId] || { twistscrape: [], brand_custom: [] };
                            const isLoadingUrls = loadingUrls[rowId];

                            return (
                              <React.Fragment key={rowId || index}>
                                <tr
                                  className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                                  onClick={() => {
                                    if (product.is_custom) {
                                      window.open(`/product/custom/${product.custom_product_id}`, '_blank');
                                    } else {
                                      window.open(`/product/${product.shopify_id}`, '_blank');
                                    }
                                  }}
                                >
                                  <td className="px-4 py-3 align-middle text-left">
                                    <div className="flex items-center gap-3">
                                      {product.v_image_link ? (
                                        <img
                                          src={product.v_image_link}
                                          alt={product.p_title}
                                          className="w-10 h-10 object-cover rounded border flex-shrink-0"
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 bg-muted rounded border flex items-center justify-center flex-shrink-0">
                                          <Package className="h-5 w-5 text-muted-foreground" />
                                        </div>
                                      )}
                                      <div className="min-w-0">
                                        <p className="font-medium text-sm truncate max-w-xs">{product.p_title || t('noTitle')}</p>
                                        {product.v_variant_title && (
                                          <p className="text-xs text-muted-foreground truncate">{product.v_variant_title}</p>
                                        )}
                                        {product.is_custom && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mt-1">
                                            {t('customBadge')}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 align-middle text-left text-sm text-muted-foreground font-mono">
                                    {product.v_ean || '-'}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-left text-sm">
                                    {product.v_vendor_code || '-'}
                                  </td>
                                  <td className={`px-4 py-3 align-middle text-right text-sm ${product.v_compareAtPrice ? 'font-medium' : 'text-muted-foreground'}`}>
                                    {product.v_compareAtPrice ? `€${parseFloat(product.v_compareAtPrice).toFixed(2)}` : '-'}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-center text-sm">
                                    {product.urls_count > 0 ? (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRowExpansion(rowId);
                                        }}
                                        className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                                        title={t('table.showUrls')}
                                      >
                                        <span>{product.urls_count}</span>
                                        {isExpanded ? (
                                          <ChevronDown className="h-4 w-4" />
                                        ) : (
                                          <ChevronRight className="h-4 w-4" />
                                        )}
                                      </button>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 align-middle text-center">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleFavorite(product, e);
                                      }}
                                      className="p-1.5 hover:bg-muted rounded text-yellow-500 hover:text-yellow-600 transition-colors"
                                      title={t('favorites.remove')}
                                    >
                                      <Star className="h-5 w-5 fill-current" />
                                    </button>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={6} className="p-3 bg-muted/30">
                                      <div className="space-y-3">
                                        {isLoadingUrls ? (
                                          <div className="flex items-center justify-center py-4">
                                            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                                          </div>
                                        ) : (
                                          <>
                                            {/* Twistscrape URLs */}
                                            {urls.twistscrape && urls.twistscrape.length > 0 && (
                                              <div>
                                                <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                                                  {t('urls.urls')}
                                                </h4>
                                                <div className="space-y-2">
                                                  {urls.twistscrape
                                                    .sort((a, b) => (a.reseller_domain || '').localeCompare(b.reseller_domain || ''))
                                                    .map((urlItem, idx) => (
                                                    <div key={idx} className="flex items-center gap-3 p-2 border rounded text-sm">
                                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        {urlItem.reseller_domain && getResellerFavicon(urlItem.reseller_domain) ? (
                                                          <img
                                                            src={getResellerFavicon(urlItem.reseller_domain)}
                                                            alt={t('resellerFavicon')}
                                                            className="w-4 h-4 rounded flex-shrink-0"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                          />
                                                        ) : (
                                                          <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                        )}
                                                        <span className="font-medium truncate">{urlItem.reseller_domain || t('unknownReseller')}</span>
                                                      </div>
                                                      <a
                                                        href={urlItem.original_url || urlItem.l_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline truncate max-w-md"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={urlItem.original_url || urlItem.l_url}
                                                      >
                                                        {urlItem.original_url || urlItem.l_url}
                                                      </a>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {/* Custom URLs */}
                                            {urls.brand_custom && urls.brand_custom.length > 0 && (
                                              <div>
                                                <h4 className="text-sm font-semibold mb-2 text-muted-foreground">
                                                  {t('customUrls')}
                                                </h4>
                                                <div className="space-y-2">
                                                  {urls.brand_custom.map((urlItem) => (
                                                    <div key={urlItem.id} className="flex items-center gap-3 p-2 border rounded text-sm">
                                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        {urlItem.reseller_image ? (
                                                          <img
                                                            src={urlItem.reseller_image}
                                                            alt={t('resellerFavicon')}
                                                            className="w-4 h-4 rounded flex-shrink-0"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                          />
                                                        ) : (
                                                          <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                        )}
                                                        <span className="font-medium truncate">{urlItem.reseller_name || t('unknownReseller')}</span>
                                                      </div>
                                                      <a
                                                        href={urlItem.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary hover:underline truncate max-w-md"
                                                        onClick={(e) => e.stopPropagation()}
                                                        title={urlItem.url}
                                                      >
                                                        {urlItem.url}
                                                      </a>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}

                                            {/* No URLs message */}
                                            {(!urls.twistscrape || urls.twistscrape.length === 0) &&
                                             (!urls.brand_custom || urls.brand_custom.length === 0) && (
                                              <p className="text-sm text-muted-foreground text-center py-2">
                                                {t('urls.noUrls')}
                                              </p>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls for Favorites (server-side) */}
                    {favoritesPagination.count > 0 && (
                      <div className="flex items-center justify-between mt-4 px-2">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                          <select
                            value={favoritesItemsPerPage}
                            onChange={(e) => {
                              setFavoritesItemsPerPage(Number(e.target.value));
                              setFavoritesPage(1);
                            }}
                            className="border rounded px-2 py-1 text-sm bg-background"
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFavoritesPage(favoritesPage - 1)}
                            disabled={favoritesPage === 1}
                            className="px-3"
                          >
                            {t('pagination.previous')}
                          </Button>

                          {/* Page Numbers */}
                          {Array.from({ length: Math.min(favoritesTotalPages, 5) }, (_, i) => {
                            let pageNum;
                            if (favoritesTotalPages <= 5) {
                              pageNum = i + 1;
                            } else if (favoritesPage <= 3) {
                              pageNum = i + 1;
                            } else if (favoritesPage >= favoritesTotalPages - 2) {
                              pageNum = favoritesTotalPages - 4 + i;
                            } else {
                              pageNum = favoritesPage - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNum}
                                variant={favoritesPage === pageNum ? "default" : "ghost"}
                                size="sm"
                                onClick={() => setFavoritesPage(pageNum)}
                                className="w-9 h-9 p-0"
                              >
                                {pageNum}
                              </Button>
                            );
                          })}

                          {favoritesTotalPages > 5 && favoritesPage < favoritesTotalPages - 2 && (
                            <>
                              <span className="px-2 text-muted-foreground">...</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFavoritesPage(favoritesTotalPages)}
                                className="w-9 h-9 p-0"
                              >
                                {favoritesTotalPages}
                              </Button>
                            </>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFavoritesPage(favoritesPage + 1)}
                            disabled={favoritesPage === favoritesTotalPages}
                            className="px-3"
                          >
                            {t('pagination.next')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="priceMonitoring" className="space-y-6">
              {/* Statistics cards - Prevent horizontal overflow when labels are longer in some languages */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* 1. Total products */}
                <Card className="w-full">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                          {tDashboard('stats.totalProducts')}
                        </CardTitle>
                        <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </CardHeader>
                      <CardContent>
                        {statsLoading ? (
                          <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                        ) : (
                          <div className="text-2xl font-bold">
                            {dashboardStats.total_products}
                            {dashboardStats.total_products_all > 0 && (
                              <span className="text-sm text-muted-foreground ml-2">/ {dashboardStats.total_products_all}</span>
                            )}
                </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {tDashboard('stats.totalProductsDesc')}
                        </p>
                      </CardContent>
                    </Card>

                    {/* 2. Changes vs yesterday */}
                    <Card className="w-full">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                          {tDashboard('stats.priceChangesVSYesterday')}
                        </CardTitle>
                        <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </CardHeader>
                      <CardContent>
                        {statsLoading ? (
                          <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                        ) : (
                          <div className="text-2xl font-bold">
                            {dashboardStats.price_changes_vs_yesterday || 0}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {tDashboard('stats.priceChangesVSYesterdayDesc')}
                        </p>
                      </CardContent>
                    </Card>

                    {/* 3. Average discount */}
                    <Card className="w-full">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                          {tDashboard('stats.avgDiscount')}
                        </CardTitle>
                        <TicketPercent className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </CardHeader>
                      <CardContent>
                        {statsLoading ? (
                          <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                        ) : (
                          <div className={`text-2xl font-bold ${dashboardStats.avg_discount > 0 ? 'text-red-600' : dashboardStats.avg_discount < 0 ? 'text-green-600' : ''}`}>
                            {dashboardStats.avg_discount < 0 ? '+' : ''}{Math.abs(dashboardStats.avg_discount).toFixed(2)}%
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {tDashboard('stats.avgDiscountDesc')}
                        </p>
                      </CardContent>
                    </Card>

                    {/* 4. Active resellers */}
                    <Card className="w-full">
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium line-clamp-2 min-h-[2.5rem]">
                          {tDashboard('stats.totalResellers')}
                        </CardTitle>
                        <Store className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </CardHeader>
                      <CardContent>
                        {statsLoading ? (
                          <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                        ) : (
                          <div className="text-2xl font-bold">{dashboardStats.total_resellers}</div>
                        )}
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {tDashboard('stats.totalResellersDesc')}
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Filters - In Accordion */}
                  <Accordion type="single" collapsible defaultValue="filters" className="w-full border rounded-lg">
                    <AccordionItem value="filters" className="border-none">
                      <AccordionTrigger className="text-sm font-medium px-4 py-3 hover:no-underline">
                        {tDashboard('filters.title')}
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {/* Using 24-column grid for finer control (1.5 cols = 3/24) */}
                        <div className="grid grid-cols-1 md:grid-cols-24 gap-3 pt-2" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                          {/* Date Selector - 3/24 = 1.5 cols */}
                          <div style={{ gridColumn: 'span 3' }}>
                            <label className="text-sm font-medium mb-1.5 block">{tDashboard('filters.date')}</label>
                            <select
                              value={selectedDate}
                              onChange={(e) => setSelectedDate(e.target.value)}
                              className="w-full h-10 px-2 rounded-md border border-input bg-background text-sm"
                            >
                              {availableDates.map(date => (
                                <option key={date} value={date}>
                                  {date.substring(0, 4)}-{date.substring(4, 6)}-{date.substring(6, 8)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Product Filter - 6/24 = 3 cols */}
                          <div style={{ gridColumn: 'span 6' }}>
                            <label className="text-sm font-medium mb-1.5 block">{tDashboard('filters.product')}</label>
                            <Input
                              type="text"
                              placeholder={tDashboard('filters.searchProduct')}
                              value={productFilter}
                              onChange={(e) => setProductFilter(e.target.value)}
                            />
                          </div>

                          {/* Reseller Filter - 5/24 = 2.5 cols */}
                          <div style={{ gridColumn: 'span 5' }}>
                            <label className="text-sm font-medium mb-1.5 block">{tDashboard('filters.reseller')}</label>
                            <Select value={selectedReseller || 'all'} onValueChange={setSelectedReseller}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={tDashboard('filters.allResellers')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">{tDashboard('filters.allResellers')}</SelectItem>
                                {uniqueResellers.map((reseller) => {
                                  const favicon = getResellerFavicon(reseller);
                                  return (
                                  <SelectItem key={reseller} value={reseller}>
                                      <span className="flex items-center gap-2">
                                        {favicon ? (
                                          <img
                                            src={favicon}
                                            alt={reseller}
                                            className="w-4 h-4 rounded flex-shrink-0"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <LinkIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        )}
                                        <span>{reseller}</span>
                                      </span>
                                  </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Country Filter - 3/24 = 1.5 cols */}
                          <div style={{ gridColumn: 'span 3' }}>
                            <label className="text-sm font-medium mb-1.5 block">{tDashboard('filters.country')}</label>
                            <Select value={selectedCountry || 'all'} onValueChange={setSelectedCountry}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={tDashboard('filters.allCountries')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">{tDashboard('filters.allCountries')}</SelectItem>
                                {['be', 'nl', 'fr', 'gb', 'de', 'com'].map((country) => {
                                  const countryCode = getCountryFlagCode(country);
                                  return (
                                    <SelectItem key={country} value={country}>
                                      <span className="flex items-center gap-2">
                                        {countryCode ? (
                                          <ReactCountryFlag
                                            countryCode={countryCode}
                                            svg
                                            style={{
                                              width: '1em',
                                              height: '1em',
                                            }}
                                          />
                                        ) : (
                                          <Globe className="h-4 w-4" />
                                        )}
                                        <span>{country.toUpperCase()}</span>
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Discount Min - 3/24 = 1.5 cols */}
                          <div style={{ gridColumn: 'span 3' }}>
                            <label className="text-sm font-medium mb-1.5 block whitespace-nowrap">{tDashboard('filters.discountMin')}</label>
                            <Input
                              type="number"
                              placeholder="%"
                              value={discountMin}
                              onChange={(e) => setDiscountMin(e.target.value)}
                              className="w-full"
                            />
                          </div>

                          {/* Discount Max - 3/24 = 1.5 cols */}
                          <div style={{ gridColumn: 'span 3' }}>
                            <label className="text-sm font-medium mb-1.5 block whitespace-nowrap">{tDashboard('filters.discountMax')}</label>
                            <Input
                              type="number"
                              placeholder="%"
                              value={discountMax}
                              onChange={(e) => setDiscountMax(e.target.value)}
                              className="w-full"
                            />
                          </div>

                          {/* Spacer - 1/24 */}
                          <div style={{ gridColumn: 'span 1' }}></div>
                        </div>

                        {/* Second row: Checkboxes and Clear All button */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                          <div className="flex items-center gap-6">
                            {/* Only changes vs yesterday checkbox */}
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={onlyChangeVSYesterday}
                                onChange={(e) => setOnlyChangeVSYesterday(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                              />
                              <span className="text-sm font-medium">
                                {tDashboard('filters.onlyChangeVSYesterday')}
                              </span>
                            </label>

                            {/* Favorites only checkbox */}
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={favoritesOnlyFilter}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setFavoritesOnlyFilter(checked);
                                  writeFavoritesOnlyPref(user?.brand, checked);
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary flex-shrink-0"
                              />
                              <span className="text-sm font-medium flex items-center">
                                <Star className="h-4 w-4 mr-1 text-yellow-500" />
                                {tDashboard('filters.favoritesOnly')}
                              </span>
                            </label>
                          </div>

                          {/* Clear All button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearFilters}
                            className="text-xs"
                          >
                            {tDashboard('filters.clearAll')}
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Price Monitoring Table */}
                  {priceMonitoringLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredCompetitorData.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <p className="text-muted-foreground">{tDashboard('table.noData')}</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">
                          {tDashboard('table.competitorPrices')} ({competitorPagination.count})
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-2">
                          {tDashboard('table.competitorPricesDesc')}
                        </p>
                      </CardHeader>
                      <CardContent>
                        <DataTable
                          data={paginatedCompetitorData}
                          columns={priceMonitoringColumns}
                          pagination={null}
                          sorting={true}
                          manualSort={sorting}
                          onSortChange={handleSortChange}
                          onRowClick={handleProductClick}
                          clickableRows
                          compact={true}
                        />

                        {/* Pagination Controls for Price Monitoring */}
                        {competitorPagination.count > 0 && (
                          <div className="flex items-center justify-between mt-4 px-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                              <select
                                value={priceMonitoringItemsPerPage}
                                onChange={(e) => {
                                  setPriceMonitoringItemsPerPage(Number(e.target.value));
                                  setPriceMonitoringPage(1);
                                }}
                                className="border rounded px-2 py-1 text-sm bg-background"
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                              </select>
                            </div>
                            
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPriceMonitoringPage(priceMonitoringPage - 1)}
                                disabled={priceMonitoringPage === 1}
                                className="px-3"
                              >
                                {t('pagination.previous')}
                              </Button>
                              
                              {/* Page Numbers */}
                              {Array.from({ length: Math.min(priceMonitoringTotalPages, 5) }, (_, i) => {
                                let pageNum;
                                if (priceMonitoringTotalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (priceMonitoringPage <= 3) {
                                  pageNum = i + 1;
                                } else if (priceMonitoringPage >= priceMonitoringTotalPages - 2) {
                                  pageNum = priceMonitoringTotalPages - 4 + i;
                                } else {
                                  pageNum = priceMonitoringPage - 2 + i;
                                }
                                
                                return (
                                  <Button
                                    key={pageNum}
                                    variant={priceMonitoringPage === pageNum ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setPriceMonitoringPage(pageNum)}
                                    className="w-9 h-9 p-0"
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                              
                              {priceMonitoringTotalPages > 5 && priceMonitoringPage < priceMonitoringTotalPages - 2 && (
                                <>
                                  <span className="px-2 text-muted-foreground">...</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPriceMonitoringPage(priceMonitoringTotalPages)}
                                    className="w-9 h-9 p-0"
                                  >
                                    {priceMonitoringTotalPages}
                                  </Button>
                                </>
                              )}
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPriceMonitoringPage(priceMonitoringPage + 1)}
                                disabled={priceMonitoringPage === priceMonitoringTotalPages}
                                className="px-3"
                              >
                                {t('pagination.next')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
            </TabsContent>
            
            <TabsContent value="tracked" className="space-y-6">
              {loading && !tabsLoaded.tracked ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
              {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('search.placeholder')}
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {filteredProducts.length === 0 ? (
                    <div className="border rounded-lg p-8 text-center">
                      <p className="text-muted-foreground">{t('noProducts')}</p>
                    </div>
                  ) : (
                    <>
                      <ProductsTable 
                        products={paginatedProducts}
                        columns={columns} 
                        expandedRows={expandedRows}
                        productUrlsMap={productUrlsMap}
                        loadingUrls={loadingUrls}
                        onToggleRow={toggleRowExpansion}
                        onRowClick={handleProductClick}
                        getResellerFavicon={getResellerFavicon}
                        t={t}
                        activeTab={activeTab}
                        onDeleteUrl={handleDeleteUrl}
                        compact={activeTab === 'priceMonitoring'}
                      />
                      
                      {/* Pagination Controls */}
                      {filteredProducts.length > 0 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="border rounded px-2 py-1 text-sm bg-background"
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="px-3"
                            >
                              {t('pagination.previous')}
                            </Button>
                            
                            {/* Page Numbers */}
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => setCurrentPage(pageNum)}
                                  className="w-9 h-9 p-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                            
                            {totalPages > 5 && currentPage < totalPages - 2 && (
                              <>
                                <span className="px-2 text-muted-foreground">...</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="w-9 h-9 p-0"
                                >
                                  {totalPages}
                                </Button>
                              </>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="px-3"
                            >
                              {t('pagination.next')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="missingUrls" className="space-y-6">
              {loading && !tabsLoaded.missingUrls ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
              {/* Info Message */}
              <div className="flex items-start gap-3 p-4 rounded-lg border bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-blue-800 dark:text-blue-200">
                    {t('missingUrls.infoDescription')}
                  </p>
                </div>
              </div>

              {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('search.placeholder')}
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {filteredProducts.length === 0 ? (
                    <div className="border rounded-lg p-8 text-center">
                      <p className="text-muted-foreground">{t('noProducts')}</p>
                    </div>
                  ) : (
                    <>
                      <ProductsTable 
                        products={paginatedProducts}
                        columns={columns}
                        expandedRows={expandedRows}
                        productUrlsMap={productUrlsMap}
                        loadingUrls={loadingUrls}
                        onToggleRow={toggleRowExpansion}
                        onRowClick={handleProductClick}
                        getResellerFavicon={getResellerFavicon}
                        t={t}
                        activeTab={activeTab}
                      />
                      
                      {/* Pagination Controls */}
                      {filteredProducts.length > 0 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="border rounded px-2 py-1 text-sm bg-background"
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="px-3"
                            >
                              {t('pagination.previous')}
                            </Button>
                            
                            {/* Page Numbers */}
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => setCurrentPage(pageNum)}
                                  className="w-9 h-9 p-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                            
                            {totalPages > 5 && currentPage < totalPages - 2 && (
                              <>
                                <span className="px-2 text-muted-foreground">...</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="w-9 h-9 p-0"
                                >
                                  {totalPages}
                                </Button>
                              </>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="px-3"
                            >
                              {t('pagination.next')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="lamptwistProducts" className="space-y-6">
              {loading && !tabsLoaded.lamptwistProducts ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {/* Info message */}
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      {urlFilter === 'all'
                        ? t('lamptwistProductsInfoAll')
                        : urlFilter === 'with_urls'
                        ? t('lamptwistProductsInfoWithData')
                        : t('lamptwistProductsInfoWithoutData')
                      }
                    </p>
                  </div>

                  {/* URL Filter Buttons */}
                  <div className="flex gap-2 mb-4">
                    <Button
                      variant={urlFilter === 'all' ? 'default' : 'outline'}
                      onClick={() => setUrlFilter('all')}
                    >
                      {t('filters.allProducts')}
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.lamptwistProducts || 0}
                      </Badge>
                    </Button>
                    <Button
                      variant={urlFilter === 'with_urls' ? 'default' : 'outline'}
                      onClick={() => setUrlFilter('with_urls')}
                    >
                      {t('filters.withUrls')}
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.tracked || 0}
                      </Badge>
                    </Button>
                    <Button
                      variant={urlFilter === 'without_urls' ? 'default' : 'outline'}
                      onClick={() => setUrlFilter('without_urls')}
                    >
                      {t('filters.withoutUrls')}
                      <Badge variant="secondary" className="ml-2">
                        {tabCounts.missingUrls || 0}
                      </Badge>
                    </Button>
                  </div>

                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('search.placeholder')}
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {filterLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="border rounded-lg p-8 text-center">
                      <p className="text-muted-foreground">{t('noProducts')}</p>
                    </div>
                  ) : (
                    <>
                      <ProductsTable
                        products={paginatedProducts}
                        columns={columns}
                        expandedRows={expandedRows}
                        productUrlsMap={productUrlsMap}
                        loadingUrls={loadingUrls}
                        onToggleRow={toggleRowExpansion}
                        onRowClick={handleProductClick}
                        getResellerFavicon={getResellerFavicon}
                        t={t}
                        activeTab={activeTab}
                        onDeleteUrl={handleDeleteUrl}
                        compact={false}
                        sorting={sorting}
                        onSortChange={handleSortChange}
                      />

                      {/* Pagination Controls */}
                      {filteredProducts.length > 0 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="border rounded px-2 py-1 text-sm bg-background"
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>

                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="px-3"
                            >
                              {t('pagination.previous')}
                            </Button>

                            {/* Page numbers */}
                            {currentPage > 3 && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCurrentPage(1)}
                                  className="w-9 h-9 p-0"
                                >
                                  1
                                </Button>
                                {currentPage > 4 && (
                                  <span className="px-2 text-muted-foreground">...</span>
                                )}
                              </>
                            )}

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => Math.abs(page - currentPage) <= 2)
                              .map(page => (
                                <Button
                                  key={page}
                                  variant={currentPage === page ? 'default' : 'ghost'}
                                  size="sm"
                                  onClick={() => setCurrentPage(page)}
                                  className="w-9 h-9 p-0"
                                >
                                  {page}
                                </Button>
                              ))}

                            {totalPages > 5 && currentPage < totalPages - 2 && (
                              <>
                                <span className="px-2 text-muted-foreground">...</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="w-9 h-9 p-0"
                                >
                                  {totalPages}
                                </Button>
                              </>
                            )}

                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="px-3"
                            >
                              {t('pagination.next')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="customProducts" className="space-y-6">
              {loading && !tabsLoaded.custom ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {/* Info message */}
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      {t('customProductsInfo')}
                    </p>
                  </div>

                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('search.placeholder')}
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {filterLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="border rounded-lg p-8 text-center">
                      <p className="text-muted-foreground">{t('noCustomProducts')}</p>
                    </div>
                  ) : (
                    <>
                      <ProductsTable
                        products={paginatedProducts}
                        columns={columns}
                        expandedRows={expandedRows}
                        productUrlsMap={productUrlsMap}
                        loadingUrls={loadingUrls}
                        onToggleRow={toggleRowExpansion}
                        onRowClick={handleProductClick}
                        getResellerFavicon={getResellerFavicon}
                        t={t}
                        activeTab={activeTab}
                        onDeleteUrl={handleDeleteUrl}
                        compact={activeTab === 'priceMonitoring'}
                        sorting={sorting}
                        onSortChange={handleSortChange}
                      />
                      
                      {/* Pagination Controls */}
                      {filteredProducts.length > 0 && (
                        <div className="flex items-center justify-between mt-4 px-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">{t('pagination.rowsPerPage')}:</span>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="border rounded px-2 py-1 text-sm bg-background"
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="px-3"
                            >
                              {t('pagination.previous')}
                            </Button>
                            
                            {/* Page Numbers */}
                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? "default" : "ghost"}
                                  size="sm"
                                  onClick={() => setCurrentPage(pageNum)}
                                  className="w-9 h-9 p-0"
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                            
                            {totalPages > 5 && currentPage < totalPages - 2 && (
                              <>
                                <span className="px-2 text-muted-foreground">...</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="w-9 h-9 p-0"
                                >
                                  {totalPages}
                                </Button>
                              </>
                            )}
                            
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              className="px-3"
                            >
                              {t('pagination.next')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </TabsContent>
              </Tabs>
            )}
      </div>

        {/* Add URL Modal */}
        <Dialog open={showAddUrlModal} onOpenChange={setShowAddUrlModal}>
          <DialogContent className="sm:max-w-[650px]">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {t('urls.addUrlTitle')}
              </DialogTitle>
              <DialogDescription>
                {t('urls.addUrlDescription')}
              </DialogDescription>
            </DialogHeader>
            
            {/* Product info with image */}
            {selectedProductForUrl && (
              <div className="flex items-center gap-3 px-6 py-3 bg-muted/30 rounded-lg">
                {selectedProductForUrl.v_image_link ? (
                  <img
                    src={selectedProductForUrl.v_image_link}
                    alt={selectedProductForUrl.p_title || t('productImage')}
                    className="w-16 h-16 object-cover rounded border"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/64';
                    }}
                  />
                ) : (
                  <div className="w-16 h-16 bg-muted rounded border flex items-center justify-center">
                    <Package className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {selectedProductForUrl.p_title || t('productImage')}
                  </div>
                  {selectedProductForUrl.v_variant_title && (
                    <div className="text-xs text-muted-foreground truncate">
                      {selectedProductForUrl.v_variant_title}
                    </div>
                  )}
                  {selectedProductForUrl.v_vendor_code && (
                    <div className="text-xs text-muted-foreground truncate">
                      {selectedProductForUrl.v_vendor_code}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <form id="add-url-form" onSubmit={(e) => { e.preventDefault(); handleAddUrl(); }} className="space-y-4 px-6">
              {/* Info about approved resellers */}
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                <p className="text-blue-900 dark:text-blue-100 mb-2">
                  {t('urls.approvedResellersInfo')}
                </p>
                <a
                  href="/resellers?tab=supported"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  {t('urls.viewApprovedResellers')}
                </a>
              </div>
              
              {/* Multiple URL inputs */}
              <div className="space-y-3">
                <label className="text-sm font-medium block">
                  {t('urls.urls')}
                </label>
                {urlInputs.map((input, index) => (
                  <div key={input.id} className="flex gap-2 items-start">
                    <div className="flex-1 relative">
                      <Input
                        type="text"
                        placeholder={t('urls.urlPlaceholder')}
                        value={input.url}
                        onChange={(e) => handleUrlInputChange(input.id, e.target.value)}
                        className={`w-full pr-10 ${
                          input.status === 'valid' ? 'border-green-500' : 
                          input.status === 'invalid' ? 'border-red-500' : ''
                        }`}
                      />
                      {input.status === 'valid' && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                      {input.status === 'invalid' && (
                        <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                      )}
                    </div>
                    {urlInputs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeUrlInput(input.id)}
                        className="mt-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addUrlInput}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('urls.addAnother')}
                </Button>
              </div>
              
              {urlError && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm p-3 rounded-md">
                  {urlError}
                </div>
              )}
            </form>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddUrlModal(false);
                  setUrlInputs([{ id: 1, url: '', status: null }]);
                  setNewUrl({ url: '' });
                  setUrlError('');
                }}
              >
                {t('urls.cancel')}
              </Button>
              <Button
                type="submit"
                form="add-url-form"
                disabled={addingUrl || !urlInputs.some(input => input.url.trim() && input.status === 'valid')}
              >
                {addingUrl ? t('urls.adding') : t('urls.add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Report Modal */}
        <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>{t('report.title')}</DialogTitle>
            </DialogHeader>

            {hasExistingReports && (
              <div className="px-6 pt-2">
                <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                  <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-900 dark:text-blue-100">
                    {t('report.existingReportsInfo')}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3 px-6 pb-3">
              {/* Bad URL - only on Price Monitoring tab */}
              {reportTarget?.tab === 'priceMonitoring' && (
                <div
                  className="flex flex-col border rounded-lg p-3 bg-muted/30 cursor-pointer"
                  onClick={() => setReportIssues((prev) => ({ ...prev, bad_url: !prev.bad_url }))}
                >
                  <label htmlFor="issue-bad-url" className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
                    <input
                      type="checkbox"
                      id="issue-bad-url"
                      checked={reportIssues.bad_url}
                      onChange={() => {}}
                      className="pointer-events-none"
                    />
                    <span className="flex-1">{t('report.badUrl')}</span>
                    {existingReportTypes.bad_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteReport('bad_url');
                        }}
                        className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                        title={t('report.cancelReport')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </label>
                </div>
              )}

              {/* Wrong RRP - available on all tabs */}
              <div
                className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                onClick={(e) => {
                  // Don't toggle if clicking inside input field
                  if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                  setReportIssues((prev) => ({ ...prev, wrong_rrp: !prev.wrong_rrp }));
                }}
              >
                <label htmlFor="issue-wrong-rrp" className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
                  <input
                    type="checkbox"
                    id="issue-wrong-rrp"
                    checked={reportIssues.wrong_rrp}
                    onChange={() => {}}
                    className="pointer-events-none"
                  />
                  <span className="flex-1">
                    {(() => {
                      const currentRrp = reportTarget?.row?.v_compareAtPrice || reportTarget?.row?.compare_at_price;
                      return currentRrp ? t('report.wrongRrpWithPrice', { price: parseFloat(currentRrp).toFixed(2) }) : t('report.wrongRrp');
                    })()}
                  </span>
                  {existingReportTypes.wrong_rrp && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport('wrong_rrp');
                      }}
                      className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                      title={t('report.cancelReport')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </label>
                {reportIssues.wrong_rrp && (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-sm font-medium">{t('report.newRrp')}</Label>
                    <Input
                      type="number"
                      placeholder={t('report.newRrpPlaceholder')}
                      value={reportRrp}
                      onChange={(e) => setReportRrp(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Wrong EAN - available on all tabs */}
              <div
                className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                onClick={(e) => {
                  // Don't toggle if clicking inside input field
                  if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                  setReportIssues((prev) => ({ ...prev, wrong_ean: !prev.wrong_ean }));
                }}
              >
                <label htmlFor="issue-wrong-ean" className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
                  <input
                    type="checkbox"
                    id="issue-wrong-ean"
                    checked={reportIssues.wrong_ean}
                    onChange={() => {}}
                    className="pointer-events-none"
                  />
                  <span className="flex-1">
                    {(() => {
                      const currentEan = reportTarget?.row?.v_ean;
                      return currentEan ? t('report.wrongEanWithValue', { ean: currentEan }) : t('report.wrongEan');
                    })()}
                  </span>
                  {existingReportTypes.wrong_ean && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport('wrong_ean');
                      }}
                      className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                      title={t('report.cancelReport')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </label>
                {reportIssues.wrong_ean && (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-sm font-medium">{t('report.newEan')}</Label>
                    <Input
                      type="text"
                      placeholder={t('report.newEanPlaceholder')}
                      value={reportEan}
                      onChange={(e) => setReportEan(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Vendor Code - available on all tabs */}
              <div
                className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                onClick={(e) => {
                  // Don't toggle if clicking inside input field
                  if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                  setReportIssues((prev) => ({ ...prev, wrong_vendor_code: !prev.wrong_vendor_code }));
                }}
              >
                <label htmlFor="issue-wrong-vendor-code" className="flex items-center gap-2 cursor-pointer text-sm font-medium w-full" onClick={(e) => e.preventDefault()}>
                  <input
                    type="checkbox"
                    id="issue-wrong-vendor-code"
                    checked={reportIssues.wrong_vendor_code}
                    onChange={() => {}}
                    className="pointer-events-none"
                  />
                  <span className="flex-1">
                    {(() => {
                      const currentVendorCode = reportTarget?.row?.v_vendor_code;
                      return currentVendorCode ? t('report.wrongVendorCodeWithValue', { code: currentVendorCode }) : t('report.wrongVendorCode');
                    })()}
                  </span>
                  {existingReportTypes.wrong_vendor_code && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport('wrong_vendor_code');
                      }}
                      className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                      title={t('report.cancelReport')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </label>
                {reportIssues.wrong_vendor_code && (
                  <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                    <Label className="text-sm font-medium">{t('report.newVendorCode')}</Label>
                    <Input
                      type="text"
                      placeholder={t('report.newVendorCodePlaceholder')}
                      value={reportVendorCode}
                      onChange={(e) => setReportVendorCode(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeReportModal} disabled={submittingReport}>
                {t('report.cancel')}
              </Button>
              <Button
                onClick={applyReport}
                disabled={
                  submittingReport ||
                  (!reportIssues.bad_url && !reportIssues.wrong_rrp && !reportIssues.wrong_ean && !reportIssues.wrong_vendor_code) ||
                  (reportIssues.wrong_rrp && (
                    !`${reportRrp}`.trim() ||
                    parseFloat(reportRrp) <= 0 ||
                    parseFloat(reportRrp) === parseFloat(reportTarget?.row?.v_compareAtPrice || reportTarget?.row?.compare_at_price || 0)
                  )) ||
                  (reportIssues.wrong_ean && (
                    !`${reportEan}`.trim() ||
                    `${reportEan}`.trim().length < 8 ||
                    `${reportEan}`.trim() === `${reportTarget?.row?.v_ean || ''}`.trim()
                  )) ||
                  (reportIssues.wrong_vendor_code && (
                    !`${reportVendorCode}`.trim() ||
                    `${reportVendorCode}`.trim() === `${reportTarget?.row?.v_vendor_code || ''}`.trim()
                  ))
                }
              >
                {submittingReport ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('report.submitting')}
                  </>
                ) : (
                  hasExistingReports ? t('report.update') : t('report.save')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Custom Product Modal */}
        <Dialog open={showAddCustomProductModal} onOpenChange={setShowAddCustomProductModal}>
          <DialogContent className="sm:max-w-[650px]">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {t('addNewProduct')}
              </DialogTitle>
              <DialogDescription>
                {t('customProduct.addDescription')}
              </DialogDescription>
            </DialogHeader>
            
            <form id="add-custom-product-form" onSubmit={(e) => { e.preventDefault(); handleAddCustomProduct(); }} className="space-y-3 px-6">
              <div className="grid grid-cols-2 gap-3">
                {/* Product Name */}
                <div>
                  <Label htmlFor="custom-name" className="text-sm font-medium mb-1.5 block">
                    {t('customProduct.name')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="custom-name"
                    type="text"
                    placeholder={t('customProduct.namePlaceholder')}
                    value={newCustomProduct.name}
                    onChange={(e) => {
                      setNewCustomProduct({ ...newCustomProduct, name: e.target.value });
                      setCustomProductError('');
                    }}
                    required
                    className="w-full"
                  />
                </div>
                
                {/* Variant Name */}
                <div>
                  <Label htmlFor="custom-variant" className="text-sm font-medium mb-1.5 block">
                    {t('customProduct.variantName')}
                  </Label>
                  <Input
                    id="custom-variant"
                    type="text"
                    placeholder={t('customProduct.variantPlaceholder')}
                    value={newCustomProduct.variant_name}
                    onChange={(e) => setNewCustomProduct({ ...newCustomProduct, variant_name: e.target.value })}
                    className="w-full"
                  />
                </div>
                
                {/* EAN + Code fournisseur + RRP sur même ligne */}
                <div className="col-span-2 grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="custom-ean" className="text-sm font-medium mb-1.5 block">
                      {t('customProduct.ean')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="custom-ean"
                      type="text"
                      placeholder={t('customProduct.eanPlaceholder')}
                      value={newCustomProduct.ean}
                      onChange={(e) => {
                        setNewCustomProduct({ ...newCustomProduct, ean: e.target.value });
                        setCustomProductError('');
                      }}
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="custom-vendor-code" className="text-sm font-medium mb-1.5 block">
                      {t('customProduct.vendorCode')}
                    </Label>
                    <Input
                      id="custom-vendor-code"
                      type="text"
                      placeholder={t('customProduct.vendorCodePlaceholder')}
                      value={newCustomProduct.vendor_code}
                      onChange={(e) => setNewCustomProduct({ ...newCustomProduct, vendor_code: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="custom-rrp" className="text-sm font-medium mb-1.5 block">
                      {t('customProduct.rrpInclVat')}
                    </Label>
                    <Input
                      id="custom-rrp"
                      type="number"
                      step="0.01"
                      placeholder={t('customProduct.rrpPlaceholder')}
                      value={newCustomProduct.rrp_incl_vat}
                      onChange={(e) => setNewCustomProduct({ ...newCustomProduct, rrp_incl_vat: e.target.value })}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              
              {/* URLs Section */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">
                    {t('customProduct.urls')} ({t('customProduct.urlsOptional')})
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddUrlToCustomProduct}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('customProduct.addUrl')}
                  </Button>
                </div>
                
                {newCustomProduct.urls.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {newCustomProduct.urls.map((urlObj, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="text"
                          placeholder={t('urls.urlPlaceholder')}
                          value={urlObj.url}
                          onChange={(e) => handleUrlChangeForCustomProduct(index, e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveUrlFromCustomProduct(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-2.5 text-xs">
                  <p className="text-blue-900 dark:text-blue-100 mb-1.5">
                    {t('urls.approvedResellersInfo')}
                  </p>
                  <a
                    href="/resellers?tab=supported"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    {t('urls.viewApprovedResellers')}
                  </a>
                </div>
              </div>
              
              {customProductError && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm p-3 rounded-md">
                  {customProductError}
                </div>
              )}
            </form>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddCustomProductModal(false);
                  setNewCustomProduct({
                    name: '',
                    variant_name: '',
                    ean: '',
                    rrp_incl_vat: '',
                    vendor_code: '',
                    urls: []
                  });
                  setCustomProductError('');
                }}
              >
                {t('urls.cancel')}
              </Button>
              <Button
                type="submit"
                form="add-custom-product-form"
                disabled={addingCustomProduct || !newCustomProduct.name.trim() || !newCustomProduct.ean.trim()}
              >
                {addingCustomProduct ? t('customProduct.adding') : t('customProduct.add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {t('customProduct.deleteConfirmTitle')}
              </DialogTitle>
              <DialogDescription>
                {t('customProduct.deleteConfirmDescription')}
              </DialogDescription>
            </DialogHeader>
            
            {productToDelete && (
              <div className="px-6 py-4">
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                  {productToDelete.v_image_link ? (
                    <img
                      src={productToDelete.v_image_link}
                      alt={productToDelete.p_title || productToDelete.name || t('productImage')}
                      className="w-16 h-16 object-cover rounded border"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/64';
                      }}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-muted rounded border flex items-center justify-center">
                      <Package className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {productToDelete.p_title || productToDelete.name || t('productImage')}
                    </div>
                    {productToDelete.v_ean && (
                      <div className="text-xs text-muted-foreground truncate">
                        {t('eanLabel')}: {productToDelete.v_ean}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setProductToDelete(null);
                }}
                disabled={deletingProduct}
              >
                {t('cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteCustomProduct}
                disabled={deletingProduct}
              >
                {deletingProduct ? t('customProduct.deleting') : t('customProduct.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </Layout>
  );
};

export default Products;
